import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import TcaCieemgEmpregador from "../TcaEmpregador/index.js";
import "./style.css";
import InputMask from "../../components/InputMask/index.js";
import { NumericFormat } from "react-number-format";
import { useForm } from "react-hook-form";
import { TbDoorExit } from "react-icons/tb";
import { jwtDecode } from "jwt-decode";
import { API_ENDPOINTS, API_BASE_URL } from "../../../js/api.js";
import FormReview from "../../components/Compos Validation/index.js";
import MissingFieldsAlert from "../../components/Fill-inAlert/index.js";
import ToastContainer from "../../components/Toast/ToastContainer.js";
import useToast from "../../hooks/useToast.js";
import useConfirm from "../../hooks/useConfirm.js";
import DocumentUploadForm from "../../components/DocumentUploadForm/index.js";
import {
  Check,
  CheckCircle2,
  AlertCircle,
  Circle,
  Upload,
  Paperclip,
  Home,
  GraduationCap,
  IdCard,
  FileText,
} from "lucide-react";

const LINK_DOCUSIGN_TCA_CIEEMG_FALLBACK =
  "https://na2.docusign.net/Member/PowerFormSigning.aspx?PowerFormId=332d1eb3-e52c-497a-bcf9-da18ae0d0234&env=na2&acct=272fb0e3-8eca-4797-ac82-b4ba543c625f&v=2";

const STATUS_CORES = {
  PENDENTE: "#3b82f6",
  APROVADO: "#1e3a8a",
  RECUSADO: "#dc2626",
};

const formatDateBR = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const formatarCnpjCpf = (valor) => {
  if (!valor) return "";

  const nums = valor.replace(/\D/g, "").slice(0, 14);
  if (nums.length === 0) return "";
  if (nums.length <= 11) {
    return nums
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  } else {
    return nums
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
  }
};

const isTelefonePlaceholder = (valor) => {
  if (!valor) return false;
  const nums = String(valor).replace(/\D/g, "");
  if (nums.length < 8) return false;
  return /^(\d)\1+$/.test(nums);
};

const sanitizeTelefone = (valor) => {
  if (!valor) return "";
  if (isTelefonePlaceholder(valor)) return "";
  return String(valor);
};

const logEndpointError = (label, err, path) => {
  const status = err?.response?.status;
  if (status === 404 || status === 405) {
    return;
  }
  console.warn(
    `[TCA] Falha ao carregar ${label} (${path}):`,
    status || "sem status",
    err?.response?.data || err?.message,
  );
};

const formatHorarioTeoricoLabel = (h) => {
  const inicio = h?.horario_inicio;
  const fim = h?.horario_fim;
  if (!inicio || !fim) return "";
  return `${inicio} às ${fim}`;
};

const __DEV__ = process.env.NODE_ENV !== "production";
const debugLog = (...args) => {
  if (__DEV__) console.log(...args);
};

const getAutorizacao = async (empresaId) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_BASE_URL}/tca/empresa/${empresaId}/autorizacao`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  } catch (error) {
    console.error("Erro autorização:", error);
    return null;
  }
};

const parseSalario = (val) => {
  if (val === null || val === undefined) return 0;
  if (typeof val === "number") return val;
  const cleaned = String(val)
    .replace("R$", "")
    .replace(/\s/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

const getLimiteHorasAprendizagem = (cursos, cursoValor) => {
  if (!Array.isArray(cursos) || !cursoValor) return null;

  const cursoSelecionado = cursos.find((curso) => {
    if (!curso) return false;
    if (curso.valorObj) {
      const fullDesc = `${curso.valorObj.curso} — ${curso.valorObj.carga_horaria} (${curso.valorObj.modalidade})`;
      return (
        fullDesc === cursoValor ||
        curso.valorObj.curso === cursoValor ||
        String(curso.id) === String(cursoValor)
      );
    }
    return (
      String(curso.id) === String(cursoValor) || curso.curso === cursoValor
    );
  });

  if (!cursoSelecionado) return null;

  const cargaHoraria = Number(
    cursoSelecionado.valorObj?.carga_horaria ??
    cursoSelecionado.carga_horaria ??
    cursoSelecionado.cargaHoraria
  );

  if (cargaHoraria === 1936) return 4;
  if (cargaHoraria === 2230) return 6;
  return null;
};

const TCA = () => {
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  let tipoPrograma = 1;
  try {
    const decoded = jwtDecode(token);
    tipoPrograma = Number(decoded.tipo_programa);
  } catch (e) {
    window.location.href = "/";
    return null;
  }

  if (tipoPrograma === 2) {
    return <TcaCieemgEmpregador />;
  }

  return <TcaComum />;
};

// ============================================
// TCA Comum (formato antigo, /tca)
// ============================================
const TcaComum = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const isStudent = Number(user?.acesso_autorizacao) === 1;
  const isEmpresaReadOnly = [0, 1].includes(Number(user?.acesso_autorizacao));
  const submittingRef = useRef(false);
  const {
    toasts,
    error: toastError,
    success: toastSuccess,
    warning: toastWarning,
    removeToast,
  } = useToast();
  const {
    confirm,
    prompt: promptUser,
    confirmDialog: confirmDialogComum,
  } = useConfirm();
  const [autorizado, setAutorizado] = useState(false);
  const [formData, setFormData] = useState({
    empresa_id: "",
    razao_social: "",
    cnpj_ou_cpf: "",
    email_empresa: "",
    nome_empresa: "",
    cargo_empresa: "",
    telefone_empresa_primario: "",
    telefone_empresa: "",
    data_inicio_contrato: "",
    data_fim_contrato: "",
    salario: "",
    tutor_aprendiz_empresa: "",
    cargo_tutor: "",
    nome_tutor: "",
    email_tutor: "",
    email_empresa_contratante: "",
    endereco_atividade_teorica: "",
    regiao: "",
    endereco_tutor: "",
    carga_horario_curso: "",
    curso: "",
    nome_aprendiz: "",
    data_nascimento: "",
    telefone_contato: "",
    email_aprendiz: "",
    atividades_aprendiz: "",
    local_realizacao_atividades: "",
    nome_aprendiz_menor_idade: "",
    telefone_aprendiz_menor_idade: "",
    email_aprendiz_menor_idade: "",
    cpf_aprendiz_menor_idade: "",
    observacoes1: "",
    observacoes2: "",
    horario_inicio_horas: "",
    horario_inicio_minutos: "",
    horario_fim_horas: "",
    horario_fim_minutos: "",
    local_execucao_diferente: false,
    emp_local_razao_social: "",
    emp_local_cnpj: "",
    emp_local_telefone: "",
    emp_local_email: "",
    emp_local_endereco: "",
    emp_local_bairro: "",
    emp_local_cidade: "",
    emp_local_uf: "",
    emp_local_cep: "",
    emp_local_rep_nome: "",
    emp_local_rep_cargo: "",
    emp_local_rep_telefone: "",
    emp_local_rep_email: "",
    resp_admin_nome: "",
    resp_admin_cargo: "",
    resp_admin_telefone: "",
    resp_admin_email: "",
    nome_filial: "",
    cpfCnpj: "",
    telefones: "",
    telefones2: "",
    bairro: "",
    municipio: "",
    uf: "",
    emailContratos: "",
    emailFinanceiro: "",
    representadaPor: "",
    cargoRepresentante: "",
    emailRepresentante: "",
    responsavelSupervisor: "",
    cargoSupervisor: "",
    emailSupervisor: "",
  });

  const [permissao, setPermissao] = useState({});
  const [showObs1, setShowObs1] = useState(false);
  const [showObs2, setShowObs2] = useState(false);
  const matrizEnderecoTeoricoRef = useRef("");
  const [filiais, setFiliais] = useState([]);
  const [mostrarFiliais, setMostrarFiliais] = useState(false);
  const [filialLoading, setFilialLoading] = useState(false);
  const [dadosFilialSelecionada, setDadosFilialSelecionada] = useState(null);
  const [localAtividade, setLocalAtividade] = useState("");
  const [anexosFiles, setAnexosFiles] = useState({});
  const [anexosProgress, setAnexosProgress] = useState({});
  const [anexosStatus, setAnexosStatus] = useState({});
  const [idadeCalculada, setIdadeCalculada] = useState(null);
  const carregouEmpresaRef = useRef(false);
  const horarioValidoAlertadoRef = useRef(false);
  const [showReview, setShowReview] = useState(false);
  const [missingFieldsAlert, setMissingFieldsAlert] = useState([]);
  const [salarioMinimoVigente, setSalarioMinimoVigente] = useState(1621.0);
  const [estudanteNaoEncontrado, setEstudanteNaoEncontrado] = useState(false);
  const [cienteCadastro, setCienteCadastro] = useState(false);
  const [unidadesCieemg, setUnidadesCieemg] = useState([]);
  const [horariosTeoricos, setHorariosTeoricos] = useState([]);

  // useEffect: carrega dados da filial via GET /filiais/:id quando o usuário
  // seleciona uma FILIAL numérica (replicado do TcaEmpregador — Tipo 2).
  useEffect(() => {
    const filialId = String(localAtividade || "").trim();
    if (!filialId || filialId === "MATRIZ" || isNaN(Number(filialId))) {
      // Limpa os campos de filial quando vazio/MATRIZ
      const camposFilial = [
        "nome_filial",
        "cpfCnpj",
        "telefones",
        "telefones2",
        "bairro",
        "municipio",
        "uf",
        "emailContratos",
        "emailFinanceiro",
        "representadaPor",
        "cargoRepresentante",
        "emailRepresentante",
        "responsavelSupervisor",
        "cargoSupervisor",
        "emailSupervisor",
      ];
      setFormData((prev) => {
        const next = { ...prev };
        camposFilial.forEach((c) => {
          next[c] = "";
        });
        if (matrizEnderecoTeoricoRef.current) {
          next.endereco_atividade_teorica = matrizEnderecoTeoricoRef.current;
        }
        return next;
      });
      return;
    }

    let cancelled = false;

    const carregarFilial = async () => {
      setFilialLoading(true);
      try {
        const token = localStorage.getItem("token");
        const url = API_ENDPOINTS.filial_id(filialId);
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cancelled) return;

        const dados = response.data || {};

        const mapeamento = [
          ["nome_filial", dados.nome_filial],
          ["cpfCnpj", dados.cnpj],
          ["telefones", dados.telefone_1],
          ["telefones2", dados.telefone_2],
          ["bairro", dados.bairro],
          ["municipio", dados.cidade],
          ["uf", dados.uf],
          ["emailContratos", dados.email_contratos],
          ["emailFinanceiro", dados.email_financeiro],
          ["representadaPor", dados.representante_nome],
          ["cargoRepresentante", dados.representante_cargo],
          ["emailRepresentante", dados.representante_email],
          ["responsavelSupervisor", dados.supervisor_nome],
          ["cargoSupervisor", dados.supervisor_cargo],
          ["emailSupervisor", dados.supervisor_email],
          ["endereco_atividade_teorica", dados.unidadeTeorica ? dados.unidadeTeorica.endereco : ""],
        ];

        setFormData((prev) => {
          const next = { ...prev };
          mapeamento.forEach(([campo, valor]) => {
            if (valor !== undefined && valor !== null) {
              next[campo] = valor;
            }
          });
          return next;
        });

        toastSuccess("Dados da filial carregados.");
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        const backendMsg =
          (Array.isArray(err.response?.data?.errors) &&
            err.response.data.errors.join(", ")) ||
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          null;
        console.error(
          `[TCA][Filial] erro ao carregar filial id=${filialId} status=${status}:`,
          err.response?.data,
        );
        if (status === 404) {
          toastError(
            backendMsg
              ? `Filial não encontrada: ${backendMsg}`
              : "Filial não encontrada",
          );
        } else if (status >= 500) {
          toastError(
            backendMsg
              ? `Erro no servidor ao buscar filial: ${backendMsg}`
              : "Erro no servidor ao buscar filial",
          );
        } else if (status === 401 || status === 403) {
          toastError("Sessão expirada ou sem permissão. Faça login novamente.");
        } else {
          toastError(
            backendMsg
              ? `Erro ao carregar filial: ${backendMsg}`
              : "Erro ao carregar dados da filial",
          );
        }
      } finally {
        if (!cancelled) setFilialLoading(false);
      }
    };

    carregarFilial();
    return () => {
      cancelled = true;
    };
  }, [localAtividade, toastError, toastSuccess]);

  const buildReviewData = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const nivel = Number(user?.acesso_autorizacao ?? 0);
    const isEmpresa =
      user?.tipo === "empresa" ||
      user?.tipo === "usuario_externo" ||
      (user?.tipo === "usuario_ciee" && nivel === 0);
    const isEstudante =
      user?.tipo === "estudante" ||
      (user?.tipo === "usuario_ciee" && nivel === 1);
    const isCiee = user?.tipo === "usuario_ciee" && nivel >= 2;

    const sections = [];

    if (isEmpresa || isCiee) {
      sections.push({
        id: "empregador",
        section: "Dados do Empregador",
        fields: [
          {
            label: "Razão Social",
            value: formData.razao_social,
            complete: !!formData.razao_social?.trim(),
          },
          {
            label: "CNPJ ou CPF",
            value: formData.cnpj_ou_cpf,
            complete: !!formData.cnpj_ou_cpf?.trim(),
          },
          {
            label: "Representante Legal",
            value: formData.nome_empresa,
            complete: !!formData.nome_empresa?.trim(),
          },
          {
            label: "Cargo do Representante",
            value: formData.cargo_empresa,
            complete: !!formData.cargo_empresa?.trim(),
          },
          {
            label: "Data de início do contrato",
            value: formatDateBR(formData.data_inicio_contrato),
            complete: !!formData.data_inicio_contrato,
          },
          {
            label: "Salário",
            value: formData.salario,
            complete: !!formData.salario && formData.salario !== "R$ 0,00",
          },
        ],
      });
    }

    if (isEstudante || isCiee) {
      const aprendizFields = [
        {
          label: "Nome do Aprendiz",
          value: formData.nome_aprendiz,
          complete: !!formData.nome_aprendiz?.trim(),
        },
        {
          label: "Telefone do Aprendiz",
          value: formData.telefone_contato,
          complete: !!formData.telefone_contato?.trim(),
        },
        {
          label: "E-mail do Aprendiz",
          value: formData.email_aprendiz,
          complete: !!formData.email_aprendiz?.trim(),
        },
        {
          label: "Curso",
          value: formData.curso,
          complete: !!formData.curso?.trim(),
        },
        {
          label: "Atividades do Aprendiz",
          value: formData.atividades_aprendiz,
          complete: !!formData.atividades_aprendiz?.trim(),
        },
      ];
      if (idadeCalculada !== null && idadeCalculada < 18) {
        aprendizFields.push(
          {
            label: "Responsável Legal",
            value: formData.nome_aprendiz_menor_idade,
            complete: !!formData.nome_aprendiz_menor_idade?.trim(),
          },
          {
            label: "Telefone do Responsável",
            value: formData.telefone_aprendiz_menor_idade,
            complete: !!formData.telefone_aprendiz_menor_idade?.trim(),
          },
          {
            label: "E-mail do Responsável",
            value: formData.email_aprendiz_menor_idade,
            complete: !!formData.email_aprendiz_menor_idade?.trim(),
          },
          {
            label: "CPF do Responsável",
            value: formData.cpf_aprendiz_menor_idade,
            complete: !!formData.cpf_aprendiz_menor_idade?.trim(),
          },
        );
      }
      sections.push({
        id: "aprendiz",
        section: "Dados do Aprendiz",
        fields: aprendizFields,
      });
    }

    sections.push({
      id: "local",
      section: "Local de Atividade",
      fields: [
        {
          label: "Local de realização das atividades",
          value:
            localAtividade === "MATRIZ" ? "Matriz" : localAtividade || "Matriz",
          complete: true,
        },
      ],
    });

    return sections;
  };

  const buildMissingFields = () => {
    const items = [];
    const user = JSON.parse(localStorage.getItem("user"));
    const nivel = Number(user?.acesso_autorizacao ?? 0);
    const isEmpresa =
      user?.tipo === "empresa" ||
      user?.tipo === "usuario_externo" ||
      (user?.tipo === "usuario_ciee" && nivel === 0);
    const isEstudante =
      user?.tipo === "estudante" ||
      (user?.tipo === "usuario_ciee" && nivel === 1);
    const isCiee = user?.tipo === "usuario_ciee" && nivel >= 2;

    if (isEmpresa || isCiee) {
      if (!formData.razao_social?.trim())
        items.push({ id: "razao_social", label: "Razão Social" });
      if (!formData.cnpj_ou_cpf?.trim())
        items.push({ id: "cnpj_ou_cpf", label: "CNPJ ou CPF" });
      if (!formData.nome_empresa?.trim())
        items.push({
          id: "nome_empresa",
          label: "Nome do representante legal",
        });
      if (!formData.cargo_empresa?.trim())
        items.push({ id: "cargo_empresa", label: "Cargo do representante" });
      if (!formData.data_inicio_contrato)
        items.push({
          id: "data_inicio_contrato",
          label: "Data início contrato",
        });
      if (!formData.salario || formData.salario === "R$ 0,00")
        items.push({ id: "salario", label: "Salário" });
    }

    if (isEstudante || isCiee) {
      if (!formData.curso?.trim()) items.push({ id: "curso", label: "Curso" });
      if (!formData.nome_aprendiz?.trim())
        items.push({ id: "nome_aprendiz", label: "Nome completo do aprendiz" });
      if (!formData.telefone_contato?.trim())
        items.push({ id: "telefone_contato", label: "Telefone do aprendiz" });
      if (!formData.email_aprendiz?.trim())
        items.push({ id: "email_aprendiz", label: "E-mail do aprendiz" });
      if (idadeCalculada !== null && idadeCalculada < 18) {
        if (!formData.nome_aprendiz_menor_idade?.trim())
          items.push({
            id: "nome_aprendiz_menor_idade",
            label: "Nome do responsável legal",
          });
        if (!formData.telefone_aprendiz_menor_idade?.trim())
          items.push({
            id: "telefone_aprendiz_menor_idade",
            label: "Telefone do responsável legal",
          });
        if (!formData.email_aprendiz_menor_idade?.trim())
          items.push({
            id: "email_aprendiz_menor_idade",
            label: "E-mail do responsável legal",
          });
        if (!formData.cpf_aprendiz_menor_idade?.trim())
          items.push({
            id: "cpf_aprendiz_menor_idade",
            label: "CPF do responsável legal",
          });
      }
    }

    return items;
  };

  const handleMissingFieldClick = (fieldId) => {
    setMissingFieldsAlert([]);
    const element =
      document.getElementById(fieldId) ||
      document.querySelector(`[name="${fieldId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => element.focus(), 500);
    }
  };

  const openReview = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();

    const missingFields = buildMissingFields();
    if (missingFields.length > 0) {
      setMissingFieldsAlert(missingFields);
      setShowReview(false);
      return;
    }

    if (formData.data_inicio_contrato && formData.data_fim_contrato) {
      const inicioParts = formData.data_inicio_contrato.split("-");
      const fimParts = formData.data_fim_contrato.split("-");

      if (inicioParts.length === 3 && fimParts.length === 3) {
        const inicio = new Date(parseInt(inicioParts[0], 10), parseInt(inicioParts[1], 10) - 1, parseInt(inicioParts[2], 10));
        const fim = new Date(parseInt(fimParts[0], 10), parseInt(fimParts[1], 10) - 1, parseInt(fimParts[2], 10));

        const limite2Anos = new Date(inicio.getFullYear() + 2, inicio.getMonth(), inicio.getDate());

        if (fim > limite2Anos) {
          toastError(
            "A data de vigência final não pode ultrapassar 2 anos a partir do início.",
          );
          setShowReview(false);
          return;
        }

        if (formData.data_nascimento) {
          const nascParts = formData.data_nascimento
            .substring(0, 10)
            .split("-");
          if (nascParts.length === 3) {
            const nasc = new Date(
              parseInt(nascParts[0], 10),
              parseInt(nascParts[1], 10) - 1,
              parseInt(nascParts[2], 10),
            );
            const limiteIdade = new Date(
              nasc.getFullYear() + 24,
              nasc.getMonth(),
              nasc.getDate(),
            );
            limiteIdade.setDate(limiteIdade.getDate() - 1); // Um dia antes do aniversário de 24 anos

            if (fim > limiteIdade) {
              toastError(
                "O contrato deve ser encerrado antes de o aprendiz completar 24 anos.",
              );
              setShowReview(false);
              return;
            }
          }
        }
      }
    }

    setMissingFieldsAlert([]);
    setShowReview(true);
  };

  useEffect(() => {
    if (!formData.data_nascimento) {
      setIdadeCalculada(null);
      return;
    }
    const hoje = new Date();
    const nasc = new Date(formData.data_nascimento);
    let idade = hoje.getFullYear() - nasc.getFullYear();
    const m = hoje.getMonth() - nasc.getMonth();
    if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) {
      idade--;
    }
    setIdadeCalculada(idade);
  }, [formData.data_nascimento]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !user) {
      window.location.href = "/";
      return;
    }

    const nivel = Number(user.acesso_autorizacao ?? 0);
    const niveisPermitidos = [0, 1, 2, 3, 4];
    if (!niveisPermitidos.includes(nivel)) {
      window.location.href = "/sem-permissao";
      return;
    }

    setFormData((prev) => ({ ...prev, empresa_id: user.empresa_id }));
    setPermissao({
      podeEditarEmpresa: [0, 2, 3, 4].includes(nivel),
      podeEditarEstudante: [1, 2, 3, 4].includes(nivel),
      podeEditarTudo: [2, 3, 4].includes(nivel),
      podeAprovar: [3, 4].includes(nivel),
      somenteLeituraEstudante: [0].includes(nivel),
    });
    setAutorizado(true);

    if (carregouEmpresaRef.current) return;
    carregouEmpresaRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const tcaIdUrl = params.get("id");

    if (user.empresa_id && !tcaIdUrl) {
      axios
        .get(`${API_BASE_URL}/tca/dados-empresa`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          setFormData((prev) => ({
            ...prev,
            empresa_id: response.data.empresa_id,
            razao_social: response.data.razao_social || "",
            cnpj_ou_cpf: formatarCnpjCpf(response.data.cnpj_ou_cpf) || "",
            telefone_empresa_primario: sanitizeTelefone(
              response.data.telefone_empresa_primario ||
              response.data.telefone ||
              "",
            ),
            email_empresa: response.data.email_empresa || "",
            regiao: response.data.regiao || "",
            endereco_atividade_teorica: response.data.endereco_atividade_teorica || "",
          }));
          if (response.data.endereco_atividade_teorica) {
            matrizEnderecoTeoricoRef.current = response.data.endereco_atividade_teorica;
          }
        })
        .catch(console.error);
    }

    if (user.empresa_id) {
      axios
        .get(`${API_BASE_URL}/tca/filiais`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          if (response.data?.possuiFiliais) {
            setFiliais(response.data.opcoes);
            setMostrarFiliais(true);
          }
        })
        .catch(console.error);
    }

    if (tcaIdUrl) {
      axios
        .get(`${API_BASE_URL}/tca/${tcaIdUrl}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          const camposTelefone = [
            "telefone_empresa_primario",
            "telefone_empresa",
            "telefone_contato",
            "telefone_tutor",
            "telefone_aprendiz_menor_idade",
            "emp_local_telefone",
            "emp_local_rep_telefone",
            "resp_admin_telefone",
          ];
          const dadosSanitizados = { ...response.data };
          camposTelefone.forEach((c) => {
            dadosSanitizados[c] = sanitizeTelefone(dadosSanitizados[c]);
          });
          setFormData((prev) => ({
            ...prev,
            ...dadosSanitizados,
            cnpj_ou_cpf: formatarCnpjCpf(response.data.cnpj_ou_cpf) || "",
            id: response.data.id,
          }));
          if (response.data.local_realizacao_atividades) {
            setLocalAtividade(
              String(response.data.local_realizacao_atividades),
            );
          }
        })
        .catch((err) => {
          console.error("Erro ao carregar TCA:", err);
          toastError("Erro ao carregar TCA.");
        });
    } else if (nivel === 1) {
      axios
        .get(`${API_BASE_URL}/tca/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          if (response.data?.id) {
            debugLog(
              `[TcaComum] Auto-redirect: /tca/me retornou TCA #${response.data.id}`,
            );
            window.location.href = `/tca?id=${response.data.id}`;
          }
        })
        .catch((err) => {
          if (err.response?.status === 404) {
            debugLog(
              "[TcaComum] Estudante sem TCA ainda — mostrando Aguardando Empresa",
            );
          } else {
            debugLog("[TcaComum] Erro no /tca/me:", err.message);
          }
        });
    }
  }, []);

  const [datasVigenciaFuturas, setDatasVigenciaFuturas] = useState([]);
  const [cursosFiltrados, setCursosFiltrados] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem("token");
    const authHeader = { Authorization: `Bearer ${token}` };

    axios
      .get(`${API_BASE_URL}/tca-configuracoes/datas-vigencia/futuras`, {
        headers: authHeader,
      })
      .then((response) => {
        const ordenadas = (response.data || []).slice().sort((a, b) => {
          const da = a?.valorObj?.data || "";
          const db = b?.valorObj?.data || "";
          return da.localeCompare(db); // "YYYY-MM-DD" → comparação lexicográfica = cronológica
        });
        setDatasVigenciaFuturas(ordenadas);
      })
      .catch((err) =>
        logEndpointError(
          "datas de vigência",
          err,
          "/tca-configuracoes/datas-vigencia/futuras",
        ),
      );
    axios
      .get(`${API_BASE_URL}/admin-unidades`, { headers: authHeader })
      .then((response) => setUnidadesCieemg(response.data || []))
      .catch((err) =>
        logEndpointError("unidades cieemg", err, "/admin-unidades"),
      );
    axios
      .get(`${API_BASE_URL}/tca-configuracoes/horarios-teoricos`, {
        headers: authHeader,
      })
      .then((response) => {
        setHorariosTeoricos(response.data || []);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404 || status === 405) return;
        console.warn(
          "[TCA] Falha ao carregar horários teóricos (/tca-configuracoes/horarios-teoricos):",
          status || "sem status",
          err?.response?.data || err?.message,
        );
      });
    axios
      .get(`${API_BASE_URL}/tca-configuracoes/cursos`, { headers: authHeader })
      .then((response) => {
        setCursosFiltrados(response.data || []);
      })
      .catch((err) =>
        logEndpointError("cursos", err, "/tca-configuracoes/cursos"),
      );
    axios
      .get(`${API_BASE_URL}/tca-configuracoes/salario-minimo`, {
        headers: authHeader,
      })
      .then((response) => {
        let valorEncontrado = null;
        if (response.data && response.data.vigente && response.data.vigente.valor !== undefined) {
          valorEncontrado = response.data.vigente.valor;
        } else if (Array.isArray(response.data) && response.data.length > 0) {
          valorEncontrado = response.data[response.data.length - 1].valor;
        } else if (response.data && response.data.valor !== undefined) {
          valorEncontrado = response.data.valor;
        }
        if (valorEncontrado) setSalarioMinimoVigente(Number(valorEncontrado));
      })
      .catch((err) =>
        logEndpointError(
          "salário mínimo",
          err,
          "/tca-configuracoes/salario-minimo",
        ),
      );
  }, []);

  // --- MÁGICA DA MATRIZ E RESET: PREENCHIMENTO AUTOMÁTICO DO ENDEREÇO TEÓRICO ---
  useEffect(() => {
    const local = formData.local_realizacao_atividades || "";

    // 1. Reset se escolher a opção "Selecionar..." (vazio)
    if (local === "") {
      setFormData((prev) => ({
        ...prev,
        endereco_atividade_teorica: "",
      }));
      return;
    }

    // 2. Forçar preenchimento com dados da Matriz se for "MATRIZ"
    if (local === "MATRIZ") {
      let enderecoMatriz = "";
      if (formData.regiao && unidadesCieemg.length > 0) {
        const unidadeEncontrada = unidadesCieemg.find(
          (u) => Number(u.codigo) === Number(formData.regiao) || String(u.codigo) === String(formData.regiao) || String(u.id) === String(formData.regiao) || String(u.codigo_unidade) === String(formData.regiao)
        );

        // Removemos o limite (!formData.endereco_atividade_teorica)
        // para que a troca de Filial -> Matriz FORCE a atualização para o endereço correto da Matriz
        if (unidadeEncontrada) {
          enderecoMatriz = unidadeEncontrada.endereco;
        }
      }

      if (!enderecoMatriz && matrizEnderecoTeoricoRef.current) {
        enderecoMatriz = matrizEnderecoTeoricoRef.current;
      }

      setFormData((prev) => ({
        ...prev,
        endereco_atividade_teorica: enderecoMatriz || "",
      }));
    }
  }, [formData.regiao, unidadesCieemg, formData.local_realizacao_atividades]);

  // --- BUSCA DA FILIAL E ATUALIZAÇÃO ---
  useEffect(() => {
    const filialId = String(localAtividade || "").trim();
    if (!filialId || filialId === "MATRIZ" || isNaN(Number(filialId))) {
      setDadosFilialSelecionada(null);
      return;
    }

    let cancelled = false;
    const carregarFilial = async () => {
      setFilialLoading(true);
      try {
        const token = localStorage.getItem("token");
        const url = API_ENDPOINTS.filial_id(filialId);
        const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;

        const dados = response.data || {};

        // Em vez de preencher emp_local_*, guardamos num state para exibir o card visual da Filial
        setDadosFilialSelecionada(dados);

        // Preenche APENAS o endereço teórico, se existir
        if (dados.unidadeTeorica && dados.unidadeTeorica.endereco) {
           setFormData((prev) => ({
             ...prev,
             endereco_atividade_teorica: dados.unidadeTeorica.endereco
           }));
        }

        toastSuccess("Dados da filial carregados.");
      } catch (err) {
        if (!cancelled) toastError("Erro ao buscar detalhes da filial.");
      } finally {
        if (!cancelled) setFilialLoading(false);
      }
    };
    carregarFilial();
    return () => { cancelled = true; };
  }, [localAtividade]);

  useEffect(() => {
    const municipio = formData.local_execucao_diferente
      ? formData.emp_local_cidade
      : formData.cidade;
    if (municipio) {
      const token = localStorage.getItem("token");
      const url = `${API_BASE_URL}/tca-configuracoes/cursos/municipio/${encodeURIComponent(municipio)}`;
      axios
        .get(url, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((r) => {
          const cursosMunicipio = r.data || [];
          // Faz merge aditivo: cursos do município são marcados,
          // mas a lista completa nunca é reduzida. Isso evita que
          // cursos cadastrados em outros municípios (ex: "Informática")
          // desapareçam do dropdown do estudante.
          if (cursosMunicipio.length === 0) return;
          setCursosFiltrados((prev) => {
            const ids = new Set(prev.map((c) => c.id));
            const novos = cursosMunicipio.filter((c) => !ids.has(c.id));
            return [...prev, ...novos];
          });
        })
        .catch((err) =>
          logEndpointError(
            "filtro de cursos por município",
            err,
            "/tca-configuracoes/cursos/municipio/{municipio}",
          ),
        );
    }
  }, [
    formData.cidade,
    formData.emp_local_cidade,
    formData.local_execucao_diferente,
  ]);

  const findCurso = (cursoVal) => {
    if (!cursoVal) return null;
    return cursosFiltrados.find((c) => {
      const fullDesc = `${c.valorObj.curso} — ${c.valorObj.carga_horaria} (${c.valorObj.modalidade})`;
      return (
        fullDesc === cursoVal ||
        c.valorObj.curso === cursoVal ||
        String(c.id) === String(cursoVal)
      );
    });
  };

  const [atividadesFiltradas, setAtividadesFiltradas] = useState([]);
  useEffect(() => {
    if (!formData.curso) {
      setAtividadesFiltradas([]);
      setFormData((prev) => ({ ...prev, atividades_aprendiz: "" }));
      return;
    }

    const token = localStorage.getItem("token");
    const authHeader = { Authorization: `Bearer ${token}` };

    // Garante que cursosFiltrados está populado (pode ainda estar vazio
    // se o usuário selecionou o curso antes da primeira requisição terminar)
    const ensureCursos = async () => {
      if (cursosFiltrados.length > 0) return cursosFiltrados;
      const r = await axios.get(`${API_BASE_URL}/tca-configuracoes/cursos`, {
        headers: authHeader,
      });
      const cursos = r.data || [];
      setCursosFiltrados(cursos);
      return cursos;
    };

    const resolveCursoId = (cursos) => {
      const achou = cursos.find((c) => {
        const fullDesc = `${c.valorObj.curso} — ${c.valorObj.carga_horaria} (${c.valorObj.modalidade})`;
        return (
          fullDesc === formData.curso ||
          c.valorObj.curso === formData.curso ||
          String(c.id) === String(formData.curso)
        );
      });
      return achou?.id;
    };

    (async () => {
      try {
        const cursos = await ensureCursos();
        const cursoId = resolveCursoId(cursos);
        if (!cursoId) {
          setAtividadesFiltradas([]);
          return;
        }

        // Tenta primeiro a rota filtrada (padrão REST); se não existir,
        // cai pra rota sem filtro e filtra no cliente.
        let atividades = [];
        try {
          const r = await axios.get(
            `${API_BASE_URL}/tca-configuracoes/atividades?curso_id=${encodeURIComponent(cursoId)}`,
            { headers: authHeader },
          );
          atividades = r.data || [];
        } catch (err) {
          if (err?.response?.status !== 404) throw err;
          // Fallback: rota sem filtro (algumas versões do backend só expõem GET /tca-configuracoes/atividades)
          try {
            const r2 = await axios.get(
              `${API_BASE_URL}/tca-configuracoes/atividades`,
              { headers: authHeader },
            );
            const todas = r2.data || [];
            atividades = todas.filter(
              (a) => String(a.curso_id) === String(cursoId),
            );
          } catch (err2) {
            if (err2?.response?.status !== 404) throw err2;
            // Backend do publicado não tem a rota deployada — silencioso,
            // dropdown fica vazio mas o usuário consegue salvar sem atividades.
            atividades = [];
          }
        }
        setAtividadesFiltradas(atividades);
      } catch (err) {
        console.warn("Erro ao carregar atividades:", err?.message);
        setAtividadesFiltradas([]);
      }
    })();
  }, [formData.curso, cursosFiltrados]);

  const [erroHorarioAprendizagem, setErroHorarioAprendizagem] = useState(null);
  const [horarioBloqueado, setHorarioBloqueado] = useState({
    horario_inicio_horas: false,
    horario_inicio_minutos: false,
    horario_fim_horas: false,
    horario_fim_minutos: false,
  });

  // Helper usado pelos onChange dos inputs de horário. Quando a carga
  // horária for "4 horas diárias", só aceita os pares válidos
  // (07:30–11:30 e 13:30–17:30). Em outras cargas horárias, não restringe.
  // Retorna { valor, bloqueado } onde bloqueado=true indica que o usuário
  // tentou digitar um caractere inválido (para acionar o ícone 🔒).
  const filtrarHorarioParcial = (campo, valorAtual, valorDigitado) => {
    if (formData.carga_horario_curso !== "4 horas diárias") {
      return { valor: valorDigitado, bloqueado: false };
    }

    const soDigitos = String(valorDigitado).replace(/\D/g, "");
    if (!soDigitos) return { valor: "", bloqueado: false };

    // Valores finais válidos para cada campo
    const valoresValidos = {
      horario_inicio_horas: ["07", "13"],
      horario_inicio_minutos: ["30"],
      horario_fim_horas: ["11", "17"],
      horario_fim_minutos: ["30"],
    };

    const permitidos = valoresValidos[campo] || [];
    const valorAtualLimpo = String(valorAtual || "").replace(/\D/g, "");

    let novoValor = "";
    let tentouInvalido = false;

    for (const digito of soDigitos) {
      const tentativa = novoValor + digito;

      // Verifica se a sequência parcial digitada pode ainda levar a um
      // valor válido. Ex: em horario_inicio_horas, "0" pode virar "07",
      // mas "5" não leva a nada.
      const aindaValido = permitidos.some(
        (v) => v.startsWith(tentativa) || tentativa.startsWith(v),
      );

      if (!aindaValido) {
        tentouInvalido = true;
        continue;
      }

      // Se o tamanho da tentativa ultrapassar o valor válido, rejeita
      // caracteres extras (ex: tentar digitar "073" quando o válido é "07")
      const algumPermiteExato = permitidos.includes(tentativa);
      const algumPermitePrefixo = permitidos.some((v) =>
        v.startsWith(tentativa),
      );

      if (algumPermiteExato) {
        novoValor = tentativa;
      } else if (algumPermitePrefixo) {
        novoValor = tentativa;
      } else {
        tentouInvalido = true;
      }
    }

    // Garante que não passe de 2 caracteres
    novoValor = novoValor.slice(0, 2);

    // Se o valor final não for exatamente um dos permitidos (e não estiver
    // vazio nem sendo prefixo de um válido), também bloqueia.
    if (
      novoValor.length === 2 &&
      !permitidos.includes(novoValor) &&
      !permitidos.some((v) => v.startsWith(novoValor))
    ) {
      // Reverte para o valor anterior válido
      novoValor = valorAtualLimpo.slice(0, 2);
      tentouInvalido = true;
    }

    return { valor: novoValor, bloqueado: tentouInvalido };
  };

  // Validação reativa do horário da aprendizagem prática: recalcula sempre
  // que curso, carga horária diária ou horários mudarem.
  useEffect(() => {
    const erro = validarHorarioAprendizagem();
    setErroHorarioAprendizagem(erro);

    // Exibe alerta de sucesso somente quando o horário estiver correto,
    // evitando repetição enquanto permanecer válido.
    if (erro === null) {
      if (
        formData.curso &&
        formData.carga_horario_curso &&
        formData.horario_inicio_horas &&
        formData.horario_inicio_minutos &&
        formData.horario_fim_horas &&
        formData.horario_fim_minutos &&
        !horarioValidoAlertadoRef.current
      ) {
        toastSuccess(
          "Horário da aprendizagem prática preenchido corretamente!",
        );
        horarioValidoAlertadoRef.current = true;
      }
    } else {
      // Permite exibir novamente caso o usuário altere o horário
      horarioValidoAlertadoRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.curso,
    formData.carga_horario_curso,
    formData.horario_inicio_horas,
    formData.horario_inicio_minutos,
    formData.horario_fim_horas,
    formData.horario_fim_minutos,
    cursosFiltrados,
  ]);

  // 1. Auto-seleciona a carga horária diária (4h ou 6h) ao selecionar o curso
  useEffect(() => {
    debugLog(
      "[DEBUG AUTO-SELECT] curso =",
      formData.curso,
      "cursosFiltradosLength =",
      cursosFiltrados.length,
    );
    if (formData.curso && cursosFiltrados.length > 0) {
      const cursoObj = findCurso(formData.curso);
      debugLog("[DEBUG AUTO-SELECT] found cursoObj =", cursoObj);
      if (cursoObj && cursoObj.valorObj.carga_horaria) {
        const ch = String(cursoObj.valorObj.carga_horaria).replace(/\D/g, "");
        debugLog("[DEBUG AUTO-SELECT] ch =", ch);
        let targetHours = "";
        const numHours = parseInt(ch, 10);
        if (!isNaN(numHours)) {
          if (numHours <= 1936) {
            targetHours = "4 horas diárias";
          } else {
            targetHours = "6 horas diárias";
          }
        }
        debugLog("[DEBUG AUTO-SELECT] targetHours =", targetHours);
        if (targetHours && formData.carga_horario_curso !== targetHours) {
          debugLog(
            "[DEBUG AUTO-SELECT] updating carga_horario_curso to",
            targetHours,
          );
          setFormData((prev) => ({
            ...prev,
            carga_horario_curso: targetHours,
          }));
        }
      }
    }
  }, [formData.curso, cursosFiltrados]);

  const prevSalarioCalculadoRef = useRef(null);

  // 2. Calcula o salário proporcional automaticamente com base nas horas selecionadas
  useEffect(() => {
    debugLog(
      "[DEBUG CALCULATION] carga_horario_curso =",
      formData.carga_horario_curso,
      "salarioMinimoVigente =",
      salarioMinimoVigente,
    );
    let horasDiarias = 0;
    if (formData.carga_horario_curso === "4 horas diárias") {
      horasDiarias = 4;
    } else if (formData.carga_horario_curso === "6 horas diárias") {
      horasDiarias = 6;
    }

    debugLog("[DEBUG CALCULATION] horasDiarias =", horasDiarias);

    if (horasDiarias === 4 || horasDiarias === 6) {
      const valorHora = Math.round((salarioMinimoVigente / 220) * 100) / 100;
      const horasSemanais = horasDiarias * 5;
      const valorSemanal = valorHora * horasSemanais;
      const salarioCalculado =
        Math.round(((valorSemanal * 4.4285 * 7) / 6) * 100) / 100;

      const currentSal = parseSalario(formData.salario);
      debugLog(
        "[DEBUG CALCULATION] currentSal =",
        currentSal,
        "salarioCalculado =",
        salarioCalculado,
      );

      if (
        !currentSal ||
        currentSal < salarioCalculado ||
        currentSal === prevSalarioCalculadoRef.current
      ) {
        debugLog("[DEBUG CALCULATION] updating salario to", salarioCalculado);
        setFormData((prev) => ({
          ...prev,
          salario: salarioCalculado,
        }));
      }

      prevSalarioCalculadoRef.current = salarioCalculado;
    }
  }, [formData.carga_horario_curso, salarioMinimoVigente]);

  const buscarDadosDoAprendiz = async (cpfDigitado) => {
    if (!cpfDigitado) return;
    const cpfLimpo = String(cpfDigitado).replace(/\D/g, "");

    if (cpfLimpo.length !== 11) return;

    try {
      setEstudanteNaoEncontrado(false);
      setCienteCadastro(false);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/estudantes/cpf/${cpfLimpo}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const dadosDoEstudante = response.data;
      console.log(
        "=== DADOS DO ESTUDANTE RETORNADOS PELA API ===",
        dadosDoEstudante,
      );

      if (dadosDoEstudante) {
        setFormData((prev) => ({
          ...prev,
          nome_aprendiz: dadosDoEstudante.nome_completo || "",
          telefone_contato: dadosDoEstudante.telefone || "",
          email_aprendiz: dadosDoEstudante.email || "",
          data_nascimento:
            dadosDoEstudante.dt_nascimento ||
            dadosDoEstudante.data_nascimento ||
            "",
        }));
      }
    } catch (error) {
      console.log("Estudante não encontrado ou erro na busca", error);
      setEstudanteNaoEncontrado(true);
      setCienteCadastro(false);
      setFormData((prev) => ({
        ...prev,
        nome_aprendiz: "",
        telefone_contato: "",
        email_aprendiz: "",
        data_nascimento: "",
      }));
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const cpfValue = formData.cpf_aprendiz || "";
      const cpfLimpo = String(cpfValue).replace(/\D/g, "");

      if (cpfLimpo.length < 11) {
        setEstudanteNaoEncontrado(false);
        if (cpfLimpo.length === 0) {
          setFormData((prev) => {
            if (
              prev.nome_aprendiz ||
              prev.telefone_contato ||
              prev.email_aprendiz ||
              prev.data_nascimento
            ) {
              return {
                ...prev,
                nome_aprendiz: "",
                telefone_contato: "",
                email_aprendiz: "",
                data_nascimento: "",
              };
            }
            return prev;
          });
        }
        return;
      }

      buscarDadosDoAprendiz(cpfValue);
    }, 1000);
    return () => clearTimeout(timer);
  }, [formData.cpf_aprendiz]);

  const handleChange = (e) => {
    debugLog("CHANGE:", e.target.name, "=", JSON.stringify(e.target.value));
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    if (
      name === "telefone_empresa_primario" ||
      name === "telefone_empresa" ||
      name === "telefone_contato" ||
      name === "telefone_tutor" ||
      name === "telefone_aprendiz_menor_idade" ||
      name === "emp_local_telefone" ||
      name === "emp_local_rep_telefone" ||
      name === "resp_admin_telefone"
    ) {
      newValue = String(newValue || "")
        .replace(/\D/g, "")
        .slice(0, 11);
    }

    if (
      name === "cnpj_ou_cpf" ||
      name === "emp_local_cnpj" ||
      name === "cpf_aprendiz_menor_idade" ||
      name === "cpf_aprendiz"
    ) {
      newValue = formatarCnpjCpf(newValue);
    }

    const emojiRegex = /[\p{Extended_Pictographic}]/gu;
    newValue = newValue.replace(emojiRegex, "");

    const regexLetras = /[^0-9a-zA-ZÀ-ÿ\s.,&\-/]/g;
    const camposLetras = [
      "razao_social",
      "nome_empresa",
      "cargo_empresa",
      "tutor_aprendiz_empresa",
      "cargo_tutor",
      "nome_tutor",
      "endereco_tutor",
      "nome_aprendiz",
      "nome_aprendiz_menor_idade",
      "observacoes1",
      "observacoes2",
    ];
    if (camposLetras.includes(name)) {
      newValue = newValue.replace(regexLetras, "");
    }

    const regexEmail = /[^0-9a-zA-ZÀ-ÿ@._+\-]/g;
    const camposEmail = [
      "email_empresa",
      "email_empresa_contratante",
      "email_tutor",
      "email_aprendiz",
      "email_aprendiz_menor_idade",
      "emp_local_email",
      "emp_local_rep_email",
      "resp_admin_email",
    ];
    if (camposEmail.includes(name)) {
      newValue = newValue.replace(regexEmail, "");
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const getAutorizacaoTCA = async (empresaId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/tca/empresa/${empresaId}/autorizacao`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Erro autorização:", error);
      return null;
    }
  };

  const handleAnexoChange = (key) => (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const allowed = ["application/pdf", "image/png", "image/jpeg"];

    if (!allowed.includes(file.type)) {
      toastWarning("Formato inválido. Use PDF, PNG ou JPG.");
      e.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toastWarning("Arquivo maior que 10MB.");
      e.target.value = "";
      return;
    }

    setAnexosFiles((prev) => ({ ...prev, [key]: file }));

    setAnexosStatus((prev) => ({ ...prev, [key]: "uploading" }));

    setAnexosProgress((prev) => ({ ...prev, [key]: 0 }));

    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", file);

      axios
        .post(`${API_BASE_URL}/upload`, fd, {
          headers: { Authorization: `Bearer ${token}` },

          onUploadProgress: (ev) => {
            if (ev.total) {
              const real = Math.round((ev.loaded * 100) / ev.total);

              setAnexosProgress((prev) => ({ ...prev, [key]: real }));

              if (real >= 100) {
                setAnexosStatus((prev) => ({ ...prev, [key]: "done" }));
              }
            }
          },
        })
        .catch((err) => {
          console.error("Erro upload:", err);

          setAnexosStatus((prev) => ({ ...prev, [key]: "error" }));
          toastError("Erro ao enviar arquivo. Tente novamente.");
        });
    } catch (err) {
      console.error("Erro inesperado no upload:", err);
    }
  };

  const HORARIOS_DIARIOS = {
    "4 horas diárias": 4,
    "6 horas diárias": 6,
  };

  const HORARIOS_PADRAO_PRATICA = {
    manha4h: { inicio: "07:30", fim: "11:30" },
    tarde4h: { inicio: "13:30", fim: "17:30" },
  };

  const formatarHorario = (horas, minutos) =>
    `${String(horas).padStart(2, "0")}:${String(minutos).padStart(2, "0")}`;

  const validarHorarioAprendizagem = () => {
    const camposVazios =
      !formData.horario_inicio_horas ||
      !formData.horario_inicio_minutos ||
      !formData.horario_fim_horas ||
      !formData.horario_fim_minutos;

    if (camposVazios) return null;

    const horaIni = Number(formData.horario_inicio_horas);
    const minIni = Number(formData.horario_inicio_minutos);
    const horaFim = Number(formData.horario_fim_horas);
    const minFim = Number(formData.horario_fim_minutos);

    if (
      Number.isNaN(horaIni) ||
      Number.isNaN(minIni) ||
      Number.isNaN(horaFim) ||
      Number.isNaN(minFim)
    ) {
      return "Preencha horário de início e fim válidos para a aprendizagem prática.";
    }

    if (horaIni > 23 || minIni > 59 || horaFim > 23 || minFim > 59) {
      return "Horário inválido. Use valores entre 00:00 e 23:59.";
    }

    const inicioMinutos = horaIni * 60 + minIni;
    const fimMinutos = horaFim * 60 + minFim;

    if (fimMinutos <= inicioMinutos) {
      return "O horário final da aprendizagem prática deve ser maior que o horário inicial.";
    }

    const duracaoMinutos = fimMinutos - inicioMinutos;
    const duracaoHoras = duracaoMinutos / 60;
    const inicioFormatado = formatarHorario(horaIni, minIni);
    const fimFormatado = formatarHorario(horaFim, minFim);

    if (formData.carga_horario_curso === "4 horas diárias") {
      const horariosValidos = Object.values(HORARIOS_PADRAO_PRATICA);
      const ehValido = horariosValidos.some(
        (h) => h.inicio === inicioFormatado && h.fim === fimFormatado,
      );

      if (!ehValido) {
        return "Informe o  horário exato referente ao curso: 07:30 às 11:30 ou 13:30 às 17:30.";
      }

      return null;
    }

    const horasDiariasPermitidas =
      HORARIOS_DIARIOS[formData.carga_horario_curso] || 6;

    if (duracaoHoras > horasDiariasPermitidas) {
      const horasFormatadas = duracaoHoras
        .toFixed(2)
        .replace(".", ",")
        .replace(/,(\d)0$/, ",$1")
        .replace(/,00$/, "");
      return `A duração da aprendizagem prática (${horasFormatadas}h) excede o limite permitido de ${horasDiariasPermitidas}h por dia.`;
    }

    return null;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const erroHorario = validarHorarioAprendizagem();

    if (erroHorario) {
      toastError(erroHorario);
      return;
    }

    if (estudanteNaoEncontrado) {
      toastError(
        "O contrato não poderá ser validado enquanto o cadastro do(a) aprendiz não for realizado.",
      );
      return;
    }

    if (submittingRef.current) return;
    submittingRef.current = true;

    if (
      !permissao.podeEditarEmpresa &&
      !permissao.podeEditarEstudante &&
      !permissao.podeEditarTudo
    ) {
      toastError("Sem permissão para editar.");
      submittingRef.current = false;
      return;
    }

    try {
      let horasDiarias = 0;
      if (formData.carga_horario_curso === "4 horas diárias") {
        horasDiarias = 4;
      } else if (formData.carga_horario_curso === "6 horas diárias") {
        horasDiarias = 6;
      } else if (formData.curso && cursosFiltrados.length > 0) {
        const cursoObj = cursosFiltrados.find(
          (c) => c.valorObj.curso === formData.curso,
        );
        if (cursoObj && cursoObj.valorObj.carga_horaria) {
          const ch = String(cursoObj.valorObj.carga_horaria).replace(/\D/g, "");
          if (ch === "1936") horasDiarias = 4;
          else if (ch === "2230") horasDiarias = 6;
        }
      }

      if (horasDiarias === 4 || horasDiarias === 6) {
        const valorHora = Math.round((salarioMinimoVigente / 220) * 100) / 100;
        const horasSemanais = horasDiarias * 5;
        const valorSemanal = valorHora * horasSemanais;
        const salarioMinimoCalculado =
          Math.round(((valorSemanal * 4.4285 * 7) / 6) * 100) / 100;

        const salarioInformado = parseSalario(formData.salario);
        if (salarioInformado < salarioMinimoCalculado) {
          toastError(
            `O salário informado (R$ ${salarioInformado.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}) é inferior ao salário mínimo vigente de R$ ${salarioMinimoCalculado.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} para a carga horária de ${horasDiarias} horas.`,
          );
          submittingRef.current = false;
          return;
        }
      }

      const token = localStorage.getItem("token");
      const decoded = jwtDecode(token);

      let linkAssinatura =
        "https://na2.docusign.net/Member/PowerFormSigning.aspx?PowerFormId=6f691352-9302-4819-8c46-0fb54f120c57&env=na2&acct=272fb0e3-8eca-4797-ac82-b4ba543c625f&v=2";
      let tipoAutorizacao = null;
      if (formData.empresa_id) {
        try {
          const autorizacao = await getAutorizacaoTCA(formData.empresa_id);
          if (autorizacao?.linkDocuSign)
            linkAssinatura = autorizacao.linkDocuSign;
          else if (autorizacao?.link && autorizacao.link.startsWith("http"))
            linkAssinatura = autorizacao.link;
          tipoAutorizacao =
            autorizacao?.tipoAutorizacao || autorizacao?.tipo || null;
        } catch (err) {
          console.warn("Erro ao buscar autorização:", err);
        }
      }

      const formDataLimpo = { ...formData };
      const telefoneFromState = String(formDataLimpo.telefone_contato || "");
      const telefoneFromDom =
        typeof document !== "undefined" &&
          document.getElementById("telefone_contato")
          ? String(document.getElementById("telefone_contato").value || "")
          : "";
      formDataLimpo.telefone_contato = String(
        telefoneFromState || telefoneFromDom,
      ).replace(/\D/g, "");
      [
        "email_empresa",
        "email_empresa_contratante",
        "email_tutor",
        "email_aprendiz",
        "email_aprendiz_menor_idade",
        "emp_local_email",
        "emp_local_rep_email",
        "resp_admin_email",
      ].forEach((c) => {
        if (formDataLimpo[c]) {
          formDataLimpo[c] = formDataLimpo[c]
            .replace(
              /[\p{Extended_Pictographic}\p{Emoji_Presentation}\uFE0F\u200D\u20E3\u{1F300}-\u{1F9FF}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}]/gu,
              "",
            )
            .replace(/[^0-9a-zA-ZÀ-ÿ@._+\-]/g, "");
        }
      });

      const camposTelefone = [
        "telefone_empresa_primario",
        "telefone_empresa",
        "telefone_contato",
        "telefone_tutor",
        "telefone_aprendiz_menor_idade",
        "emp_local_telefone",

        "emp_local_rep_telefone",
        "resp_admin_telefone",
      ];

      camposTelefone.forEach((c) => {
        if (!formDataLimpo[c]) return;
        const nums = String(formDataLimpo[c]).replace(/\D/g, "").slice(0, 11);
        if (nums.length <= 2) formDataLimpo[c] = nums;
        else if (nums.length <= 6)
          formDataLimpo[c] = `(${nums.slice(0, 2)})${nums.slice(2)}`;
        else if (nums.length <= 10)
          formDataLimpo[c] =
            `(${nums.slice(0, 2)})${nums.slice(2, 6)}-${nums.slice(6)}`;
        else
          formDataLimpo[c] =
            `(${nums.slice(0, 2)})${nums.slice(2, 7)}-${nums.slice(7)}`;
      });

      if (
        !formDataLimpo.telefone_empresa &&
        formDataLimpo.telefone_empresa_primario
      ) {
        formDataLimpo.telefone_empresa =
          formDataLimpo.telefone_empresa_primario;
      }

      const payload = {
        ...formDataLimpo,
        tipo_programa: Number(decoded.tipo_programa) || 1,
        tipo_autorizacao: tipoAutorizacao,
        link_docusign: linkAssinatura,
        local_realizacao_atividades: localAtividade || "MATRIZ",
      };
      const response = await axios.post(`${API_BASE_URL}/tca`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const novoId = response.data?.id || response.data?.tcaId || "?";

      // Persistir dados da Filial (PUT /filiais/:id) quando o usuário
      // selecionou FILIAL e os campos foram preenchidos.
      // Replicado do TcaEmpregador (Tipo 2).
      const filialIdNum = Number(localAtividade);
      const filialPayload = {
        nome_filial: formData.nome_filial,
        cnpj: formData.cpfCnpj,
        telefone_1: formData.telefones,
        telefone_2: formData.telefones2,
        bairro: formData.bairro,
        cidade: formData.municipio,
        uf: formData.uf,
        email_contratos: formData.emailContratos,
        email_financeiro: formData.emailFinanceiro,
        representante_nome: formData.representadaPor,
        representante_cargo: formData.cargoRepresentante,
        representante_email: formData.emailRepresentante,
        supervisor_nome: formData.responsavelSupervisor,
        supervisor_cargo: formData.cargoSupervisor,
        supervisor_email: formData.emailSupervisor,
      };
      if (
        mostrarFiliais &&
        localAtividade &&
        localAtividade !== "MATRIZ" &&
        !isNaN(filialIdNum)
      ) {
        try {
          await axios.put(
            API_ENDPOINTS.filial_update(filialIdNum),
            filialPayload,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          toastSuccess("Dados da filial atualizados.");
        } catch (filialErr) {
          console.warn(
            "Falha ao persistir filial (PUT /filiais/:id):",
            filialErr,
            filialErr?.response?.data,
          );
          const status = filialErr.response?.status;
          const backendMsg =
            filialErr.response?.data?.message ||
            filialErr.response?.data?.erro ||
            filialErr.response?.data?.error ||
            filialErr.message ||
            "Erro ao atualizar dados da filial";
          if (status === 403) {
            toastError(
              "Sem permissão para atualizar a filial. Contate o administrador do sistema.",
            );
          } else if (status === 401) {
            toastError(
              "Sessão expirada. Faça login novamente para atualizar a filial.",
            );
          } else if (status === 404) {
            toastError("Filial não encontrada no servidor.");
          } else if (status >= 500) {
            toastError(`Erro no servidor ao atualizar filial: ${backendMsg}`);
          } else {
            toastError(backendMsg);
          }
        }
      }
      const abrir = await confirm({
        title: "TCA enviado com sucesso!",
        message: `TCA #${novoId} foi enviado.\n\nDeseja abrir o DocuSign agora para assinar o contrato?`,
        confirmText: "Abrir DocuSign",
        cancelText: "Depois",
        variant: "success",
      });
      if (abrir) {
        window.open(linkAssinatura, "_blank", "noopener,noreferrer");
      } else {
        toastSuccess("TCA salvo. Você pode abrir o DocuSign depois.");
      }
    } catch (error) {
      console.error("ERRO AO SALVAR TCA:", error);

      if (
        error.response?.status === 400 &&
        error.response?.data?.campos_faltando
      ) {
        const campos = error.response.data.campos_faltando;
        const lista = campos
          .map((c, i) => `${i + 1}. ${c.replace(/_/g, " ")}`)
          .join("\n");
        alert(
          `❌ Faltam ${campos.length} campo${campos.length > 1 ? "s" : ""} obrigatório${campos.length > 1 ? "s" : ""}:\n\n${lista}`,
        );
      } else if (error.response?.data?.errors) {
        alert(`❌ Erro: ${error.response.data.errors.join("\n")}`);
      } else {
        alert("Erro ao salvar TCA");
      }
      toastError("Erro ao salvar TCA");
    } finally {
      submittingRef.current = false;
    }
  };

  const aprovar = async () => {
    const ok = await confirm({
      title: "Aprovar TCA",
      message:
        "Tem certeza que deseja APROVAR este TCA?\nEsta ação não pode ser desfeita.",
      confirmText: "Sim, aprovar",
      cancelText: "Cancelar",
      variant: "success",
    });
    if (!ok) return;

    try {
      const id = formData.id;
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_BASE_URL}/tca/${id}/aprovar`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toastSuccess("TCA aprovado!");
    } catch (error) {
      console.error(error);
      toastError(
        error.response?.data?.errors?.[0] ||
        error.response?.data?.erro ||
        "Erro ao aprovar",
      );
    }
  };

  const recusar = async () => {
    const motivo = await promptUser({
      title: "Recusar TCA",
      message: "Informe o motivo da recusa:",
      inputPlaceholder: "Ex: documentos incompletos...",
      confirmText: "Recusar TCA",
      cancelText: "Cancelar",
      variant: "danger",
    });
    if (!motivo) return;

    try {
      const id = formData.id;
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_BASE_URL}/tca/${id}/recusar`,
        { motivo },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toastWarning("TCA recusado!");
    } catch (error) {
      console.error(error);
      toastError(
        error.response?.data?.errors?.[0] ||
        error.response?.data?.erro ||
        "Erro ao recusar",
      );
    }
  };

  const handleVoltarInicio = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("empresa_id");
    window.location.href = "/";
  };

  const verificarPermissaoTCA = () => {
    const token = localStorage.getItem("token");
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      const tipo = decoded.tipo;
      const tipoPrograma = Number(decoded.tipo_programa);
      if (tipo === "usuario_ciee") return true;
      if (tipoPrograma === 1 || tipoPrograma === 2) return true;
      return false;
    } catch (e) {
      return false;
    }
  };

  if (!verificarPermissaoTCA()) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!autorizado) return null;
  if (Number(user?.acesso_autorizacao ?? 0) === 1 && !formData.id) {
    return (
      <div className="tca-container tca-py-5 tca-text-center">
        <h2 className="tca-h2">Aguardando Empresa</h2>
        <p
          className="tca-text-muted"
          style={{ maxWidth: "500px", margin: "1rem auto" }}
        >
          Você ainda não tem um TCA para preencher.
          <br />
          <br />A empresa precisa criar o TCA primeiro. Depois você poderá abrir
          aqui e preencher seus dados.
        </p>
        <button
          type="button"
          className="tca-btn tca-btn-secondary"
          onClick={handleVoltarInicio}
          style={{ marginTop: "1rem" }}
        >
          Voltar ao Início
        </button>
      </div>
    );
  }

  const municipioAtualTca = formData.municipio;

  const horariosTeoricosDisponiveis = municipioAtualTca
    ? horariosTeoricos.filter((h) => {
        const municipioNorm = String(municipioAtualTca).trim().toUpperCase();
        return (h.municipios || []).some(
          (m) => String(m).trim().toUpperCase() === municipioNorm,
        );
      })
    : [];

  return (
    <>
      <form className="form-tca form-tca-fade tca-container" onSubmit={handleSubmit}>
        <h2 className="tca-h2">TCA — Programa Aprendiz</h2>
        <div
          style={{
            marginBottom: "1rem",
            display: "flex",
            gap: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          {formData.id && (
            <button
              type="button"
              className="tca-btn tca-btn-secondary"
              onClick={async () => {
                const link = `${window.location.origin}/tca?id=${formData.id}`;
                try {
                  await navigator.clipboard.writeText(link);
                  alert(
                    "✅ Link copiado! Cole no WhatsApp/email do Estudante.\n\n" +
                    link,
                  );
                  toastSuccess(
                    `Link copiado! Cole no WhatsApp/email do Estudante. ${link}`,
                  );
                } catch (err) {
                  const ta = document.createElement("textarea");

                  ta.value = link;
                  document.body.appendChild(ta);
                  ta.select();
                  document.execCommand("copy");
                  document.body.removeChild(ta);
                  toastSuccess(`Link copiado! ${link}`);
                }
              }}
            >
              📋 Copiar link pro Estudante
            </button>
          )}

          {formData.id && formData.status && (
            <div
              className="tca-alert"
              style={{
                padding: "0.75rem 1rem",
                borderRadius: "4px",
                fontWeight: "bold",
                backgroundColor:
                  formData.status === "APROVADO"
                    ? "#d4edda"
                    : formData.status === "RECUSADO"
                      ? "#f8d7da"
                      : "#fff3cd",
                color:
                  formData.status === "APROVADO"
                    ? "#155724"
                    : formData.status === "RECUSADO"
                      ? "#721c24"
                      : "#856404",
                border:
                  "1px solid " +
                  (formData.status === "APROVADO"
                    ? "#c3e6cb"
                    : formData.status === "RECUSADO"
                      ? "#f5c6cb"
                      : "#ffeeba"),
              }}
            >
              Status atual: {formData.status}
              {formData.status === "APROVADO" &&
                " — Trava ativa. Não pode mais ser modificado."}
              {formData.status === "RECUSADO" &&
                ` — Trava ativa. Não pode mais ser modificado.${formData.motivo_recusa ? ` Motivo: ${formData.motivo_recusa}` : ""}`}
            </div>
          )}
        </div>


        {/* === SEÇÃO 1: Dados da Empresa === */}
        <div className="div-dados-empresa-title">
          <label htmlFor="dados-empresa-title">
            Dados da Empresa Contratante
          </label>
        </div>

        <div className="div-tca-one">
          <div className="div-razao-social">
            <label className="all-label" htmlFor="razao-social">
              Razão Social:
            </label>
            <input
              type="text"
              name="razao_social"
              className="razao-social"
              id="razao_social"
              disabled={isEmpresaReadOnly}
              readOnly={isEmpresaReadOnly}
              maxLength={200}
              onChange={handleChange}
              value={formData.razao_social}
            />
          </div>
        </div>

        <div className="div-tca-two">
          <div className="div-cnpj-cpf">
            <label className="all-label" htmlFor="cnpj_ou_cpf">
              CNPJ/CPF:
            </label>
            <input
              type="text"
              name="cnpj_ou_cpf"
              className="cnpj-cpf"
              id="cnpj_ou_cpf"
              disabled={isEmpresaReadOnly}
              readOnly={isEmpresaReadOnly}
              onChange={handleChange}
              maxLength={18}
              value={formatarCnpjCpf(formData.cnpj_ou_cpf)}
            />
          </div>

          <div className="div-tca-telefone">
            <label className="all-label" htmlFor="telefone_empresa_primario">
              Telefone:
            </label>
            <InputMask
              mask="(99)99999-9999"
              maskChar=" "
              type="text"
              name="telefone_empresa_primario"
              autoComplete="off"
              className="tca-telefone"
              id="telefone_empresa_primario"
              disabled={isEmpresaReadOnly}
              readOnly={isEmpresaReadOnly}
              value={formData.telefone_empresa_primario}
              onChange={handleChange}
              required
            />
          </div>

          <div className="div-tca-email">
            <label className="all-label" htmlFor="email_empresa">
              E-mail:
            </label>
            <input
              type="email"
              name="email_empresa"
              className="tca-email"
              id="email_empresa"
              disabled={isEmpresaReadOnly}
              readOnly={isEmpresaReadOnly}
              maxLength={80}
              onChange={handleChange}
              value={formData.email_empresa}
            />
          </div>
        </div>

        {/* === CAMPO UNIDADE (MATRIZ / FILIAL) === */}
        {mostrarFiliais && (
          <div className="div-tca-one" style={{ marginTop: "10px", marginBottom: "20px" }}>
            <div style={{ width: "100%" }}>
              <label className="all-label" htmlFor="unidade_select">
                Unidade:
              </label>
              <select
                id="unidade_select"
                style={{ width: "100%", padding: "5px", borderRadius: "4px", border: "1px solid #7a7a7a", backgroundColor: "#fff", height: "35px", outline: "none" }}
                value={localAtividade}
                onChange={(e) => {
                  const val = e.target.value;
                  setLocalAtividade(val);
                  setFormData((prev) => ({
                    ...prev,
                    local_realizacao_atividades: val
                  }));
                }}
                required
              >
                <option value="">Selecione</option>
                {filiais.map((f) => (
                  <option key={f.id} value={f.id === 0 ? "MATRIZ" : f.id}>
                    {f.id === 0 ? "Matriz" : "Filial"}
                  </option>
                ))}
              </select>
              {filialLoading && <span style={{ color: "#3b82f6", fontSize: "0.85rem", marginTop: "4px", display: "inline-block" }}>Carregando dados da unidade...</span>}
            </div>
          </div>
        )}

        {/* === CARD DEDICADO DA FILIAL === */}
        {dadosFilialSelecionada && (
          <>
            <div className="div-dados-empresa-title" style={{ marginTop: "1rem" }}>
              <label>Dados da Filial Selecionada</label>
            </div>

            <div className="div-tca-one">
              <div className="div-razao-social">
                <label className="all-label">Razão Social:</label>
                <input
                  type="text"
                  className="razao-social"
                  disabled={true}
                  value={dadosFilialSelecionada.nome_filial || ""}
                />
              </div>
            </div>

            <div className="div-tca-two">
              <div className="div-cnpj-cpf">
                <label className="all-label">CNPJ/CPF:</label>
                <input
                  type="text"
                  className="cnpj-cpf"
                  disabled={true}
                  value={dadosFilialSelecionada.cnpj ? formatarCnpjCpf(dadosFilialSelecionada.cnpj) : ""}
                />
              </div>
              <div className="div-tca-telefone">
                <label className="all-label">Telefone:</label>
                <input
                  type="text"
                  className="tca-telefone"
                  disabled={true}
                  value={dadosFilialSelecionada.telefone_1 || ""}
                />
              </div>
              <div className="div-tca-email">
                <label className="all-label">E-mail:</label>
                <input
                  type="text"
                  className="tca-email"
                  disabled={true}
                  value={dadosFilialSelecionada.email_contratos || ""}
                />
              </div>
            </div>

            <div className="div-tca-three">
              <div className="div-tca-row" style={{ display: "flex", gap: "10px" }}>
                <div style={{ flex: "1" }}>
                  <label className="all-label">Endereço:</label>
                  <input
                    type="text"
                    className="tca-nome"
                    disabled={true}
                    value={`${dadosFilialSelecionada.endereco || "Não informado"}, ${dadosFilialSelecionada.bairro} - ${dadosFilialSelecionada.cidade}/${dadosFilialSelecionada.uf}`}
                  />
                </div>
                <div style={{ flex: "1" }}>
                  <label className="all-label">Representante:</label>
                  <input
                    type="text"
                    className="tca-nome"
                    disabled={true}
                    value={dadosFilialSelecionada.representante_nome || ""}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* === SEÇÃO 2: Dados do Representante === */}
        <div className="div-representante-legal-title">
          <label htmlFor="representante-legal-title">
            Representante Legal (Quem irá assinar o contrato)
          </label>
        </div>

        <div className="div-tca-three">
          <div className="div-tca-nome">
            <label className="all-label" htmlFor="nome_empresa">
              Nome:
            </label>
            <input
              type="text"
              name="nome_empresa"
              className="tca-nome"
              id="nome_empresa"
              disabled={!permissao.podeEditarEmpresa}
              maxLength={100}
              onChange={handleChange}
              value={formData.nome_empresa}
            />
          </div>
          <div className="div-tca-row">
            <div className="div-tca-cargo">
              <label className="all-label" htmlFor="cargo_empresa">
                Cargo:
              </label>
              <input
                type="text"
                name="cargo_empresa"
                className="tca-cargo"
                id="cargo_empresa"
                disabled={!permissao.podeEditarEmpresa}
                maxLength={100}
                onChange={handleChange}
                value={formData.cargo_empresa}
              />
            </div>
            <div className="div-tca-telefone">
              <label className="all-label" htmlFor="telefone_empresa">
                Telefone:
              </label>
              <InputMask
                mask="(99)99999-9999"
                maskChar=" "
                type="text"
                name="telefone_empresa"
                disabled={!permissao.podeEditarEmpresa}
                autoComplete="off"
                className="tca-telefone"
                id="telefone_empresa"
                onChange={handleChange}
                value={formData.telefone_empresa || ""}
              />
            </div>
            <div className="div-tca-email">
              <label className="all-label" htmlFor="email_empresa_contratante">
                Email:
              </label>
              <input
                type="email"
                name="email_empresa_contratante"
                className="tca-email"
                id="email_empresa_contratante"
                disabled={!permissao.podeEditarEmpresa}
                maxLength={100}
                onChange={handleChange}
                value={formData.email_empresa_contratante}
              />
            </div>
          </div>
        </div>

        <div
          className="div-local-execucao-pergunta"
          style={{ marginBottom: "1rem" }}
        >
          <label style={{ fontWeight: 500, marginRight: "1rem" }}>
            O local de execução das atividades práticas é diferente do CNPJ
            informado acima?
          </label>

          <div
            className="tca-radio-group"
            style={{ display: "inline-block", marginLeft: "1rem" }}
          >
            <label
              className="tca-form-check tca-form-check-inline"
              style={{ marginRight: "1rem", cursor: "pointer" }}
            >
              <input
                type="radio"
                name="local_execucao_diferente"
                value="1"
                checked={
                  formData.local_execucao_diferente === true ||
                  formData.local_execucao_diferente === 1
                }
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    local_execucao_diferente: true,
                  }))
                }
                style={{ marginRight: "0.4rem" }}
              />
              <span>Sim</span>
            </label>

            <label
              className="tca-form-check tca-form-check-inline"
              style={{ cursor: "pointer" }}
            >
              <input
                type="radio"
                name="local_execucao_diferente"
                value="0"
                checked={
                  formData.local_execucao_diferente === false ||
                  formData.local_execucao_diferente === 0
                }
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    local_execucao_diferente: false,
                    emp_local_razao_social: "",
                    emp_local_cnpj: "",
                    emp_local_telefone: "",
                    emp_local_email: "",
                    emp_local_endereco: "",
                    emp_local_bairro: "",
                    emp_local_cidade: "",
                    emp_local_uf: "",
                    emp_local_cep: "",
                    emp_local_rep_nome: "",
                    emp_local_rep_cargo: "",
                    emp_local_rep_telefone: "",
                    emp_local_rep_email: "",
                    resp_admin_nome: "",
                    resp_admin_cargo: "",
                    resp_admin_telefone: "",
                    resp_admin_email: "",
                  }))
                }
                style={{ marginRight: "0.4rem" }}
              />
              <span>Não</span>
            </label>
          </div>
        </div>

        {Boolean(formData.local_execucao_diferente) && (
          <>
            <div className="div-dados-empresa-local-title">
              <label htmlFor="dados-empresa-local-title">
                2. Dados da Empresa Contratante (Local de Execução das
                Atividades Práticas)
              </label>
            </div>

            <div className="div-tca-three">
              <div className="div-tca-endereco" style={{ width: "100%" }}>
                <label className="all-label" htmlFor="emp_local_razao_social">
                  Razão Social:
                </label>
                <input
                  type="text"
                  name="emp_local_razao_social"
                  id="emp_local_razao_social"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={200}
                  onChange={handleChange}
                  value={formData.emp_local_razao_social}
                />
              </div>
            </div>

            <div className="div-tca-two">
              <div className="div-tca-cnpj-local">
                <label className="all-label" htmlFor="emp_local_cnpj">
                  CNPJ:
                </label>
                <input
                  type="text"
                  name="emp_local_cnpj"
                  id="emp_local_cnpj"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={18}
                  onChange={handleChange}
                  value={formatarCnpjCpf(formData.emp_local_cnpj)}
                />
              </div>
              <div className="div-tca-telefone">
                <label className="all-label" htmlFor="emp_local_telefone">
                  Telefone:
                </label>
                <InputMask
                  mask="(99)99999-9999"
                  maskChar=" "
                  type="text"
                  name="emp_local_telefone"
                  autoComplete="off"
                  id="emp_local_telefone"
                  disabled={!permissao.podeEditarEmpresa}
                  value={formData.emp_local_telefone}
                  onChange={handleChange}
                />
              </div>
              <div className="div-tca-email">
                <label className="all-label" htmlFor="emp_local_email">
                  E-mail:
                </label>
                <input
                  type="email"
                  name="emp_local_email"
                  id="emp_local_email"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={80}
                  onChange={handleChange}
                  value={formData.emp_local_email}
                />
              </div>
            </div>

            <div className="div-tca-row">
              <div className="div-tca-endereco">
                <label className="all-label" htmlFor="emp_local_endereco">
                  Endereço:
                </label>
                <input
                  type="text"
                  name="emp_local_endereco"
                  id="emp_local_endereco"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={200}
                  onChange={handleChange}
                  value={formData.emp_local_endereco}
                />
              </div>
              <div className="div-tca-bairro">
                <label className="all-label" htmlFor="emp_local_bairro">
                  Bairro:
                </label>
                <input
                  type="text"
                  name="emp_local_bairro"
                  id="emp_local_bairro"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.emp_local_bairro}
                />
              </div>
            </div>

            <div className="div-tca-row">
              <div className="div-tca-cidade" style={{ flex: "3" }}>
                <label className="all-label" htmlFor="emp_local_cidade">
                  Cidade:
                </label>
                <input
                  type="text"
                  name="emp_local_cidade"
                  id="emp_local_cidade"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.emp_local_cidade}
                />
              </div>
              <div
                className="div-tca-uf"
                style={{ flex: "1", maxWidth: "80px" }}
              >
                <label className="all-label" htmlFor="emp_local_uf">
                  UF:
                </label>
                <input
                  type="text"
                  name="emp_local_uf"
                  id="emp_local_uf"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={2}
                  onChange={handleChange}
                  value={formData.emp_local_uf}
                />
              </div>
              <div className="div-tca-cep" style={{ flex: "1.5" }}>
                <label className="all-label" htmlFor="emp_local_cep">
                  CEP:
                </label>
                <InputMask
                  mask="99999-999"
                  maskChar=" "
                  type="text"
                  name="emp_local_cep"
                  autoComplete="off"
                  id="emp_local_cep"
                  disabled={!permissao.podeEditarEmpresa}
                  value={formData.emp_local_cep}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="div-representante-legal-local-title">
              <label htmlFor="representante-legal-local-title">
                Representante Legal
              </label>
            </div>

            <div className="div-tca-three">
              <div className="div-tca-nome" style={{ width: "100%" }}>
                <label className="all-label" htmlFor="emp_local_rep_nome">
                  Nome:
                </label>
                <input
                  type="text"
                  name="emp_local_rep_nome"
                  id="emp_local_rep_nome"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.emp_local_rep_nome}
                />
              </div>
              <div className="div-tca-cargo" style={{ width: "100%" }}>
                <label className="all-label" htmlFor="emp_local_rep_cargo">
                  Cargo:
                </label>
                <input
                  type="text"
                  name="emp_local_rep_cargo"
                  id="emp_local_rep_cargo"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.emp_local_rep_cargo}
                />
              </div>
            </div>

            <div className="div-tca-row">
              <div className="div-tca-rep-telefone">
                <label className="all-label" htmlFor="emp_local_rep_telefone">
                  Telefone:
                </label>
                <InputMask
                  mask="(99)99999-9999"
                  maskChar=" "
                  type="text"
                  name="emp_local_rep_telefone"
                  autoComplete="off"
                  id="emp_local_rep_telefone"
                  disabled={!permissao.podeEditarEmpresa}
                  value={formData.emp_local_rep_telefone}
                  onChange={handleChange}
                />
              </div>
              <div className="div-tca-rep-email">
                <label className="all-label" htmlFor="emp_local_rep_email">
                  E-mail:
                </label>
                <input
                  type="email"
                  name="emp_local_rep_email"
                  id="emp_local_rep_email"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.emp_local_rep_email}
                />
              </div>
            </div>

            <div className="div-responsavel-admin-title">
              <label htmlFor="responsavel-admin-title">
                Responsável Administrativo
                <small
                  style={{
                    marginLeft: "0.5rem",
                    color: "#6c757d",
                    fontWeight: "normal",
                  }}
                >
                  DocuSign Envelope ID: 8F16BC02-BBEC-44BB-99A7-17EA297088F1
                </small>
              </label>
            </div>

            <div className="div-tca-three">
              <div className="div-tca-nome" style={{ width: "100%" }}>
                <label className="all-label" htmlFor="resp_admin_nome">
                  Nome:
                </label>
                <input
                  type="text"
                  name="resp_admin_nome"
                  id="resp_admin_nome"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.resp_admin_nome}
                />
              </div>
              <div className="div-tca-cargo" style={{ width: "100%" }}>
                <label className="all-label" htmlFor="resp_admin_cargo">
                  Cargo:
                </label>
                <input
                  type="text"
                  name="resp_admin_cargo"
                  id="resp_admin_cargo"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.resp_admin_cargo}
                />
              </div>
            </div>

            <div className="div-tca-row">
              <div className="div-tca-resp-admin-telefone">
                <label className="all-label" htmlFor="resp_admin_telefone">
                  Telefone:
                </label>
                <InputMask
                  mask="(99)99999-9999"
                  maskChar=" "
                  type="text"
                  name="resp_admin_telefone"
                  autoComplete="off"
                  id="resp_admin_telefone"
                  disabled={!permissao.podeEditarEmpresa}
                  value={formData.resp_admin_telefone}
                  onChange={handleChange}
                />
              </div>
              <div className="div-tca-resp-admin-email">
                <label className="all-label" htmlFor="resp_admin_email">
                  E-mail:
                </label>
                <input
                  type="email"
                  name="resp_admin_email"
                  id="resp_admin_email"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.resp_admin_email}
                />
              </div>
            </div>
          </>
        )}

        <div
          className="div-dados-aprendizagem-title"
          style={{ position: "relative" }}
        >
          <label htmlFor="dados-aprendizagem-title">Dados do(a) Aprendiz</label>
          {idadeCalculada !== null && (
            <span
              style={{
                position: "absolute",
                right: "16px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "0.80rem",
                padding: "4px 10px",
                borderRadius: "12px",
                backgroundColor: idadeCalculada >= 18 ? "#dcfce7" : "#fef08a",
                color: idadeCalculada >= 18 ? "#166534" : "#854d0e",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
              }}
            >
              {idadeCalculada >= 18 ? "Maior de Idade" : "Menor de Idade"}
            </span>
          )}
        </div>

        <div className="div-tca-five">

          {estudanteNaoEncontrado && (
            <div style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #f87171",
              borderLeft: "4px solid #ef4444",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              color: "#991b1b",
              padding: "1rem 1.25rem",
              borderRadius: "8px",
              marginTop: "1.5rem",
              marginBottom: "1.5rem",
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              lineHeight: "1.5"
            }}>
              <AlertCircle size={22} style={{ flexShrink: 0, marginTop: "2px", color: "#ef4444" }} />
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <span style={{ fontWeight: "700", fontSize: "0.95rem" }}>Aprendiz não encontrado</span>
                <span style={{ fontSize: "0.9rem", color: "#7f1d1d" }}>
                  O contrato não poderá ser validado enquanto o cadastro do(a) aprendiz não for realizado no sistema.
                </span>
              </div>
            </div>
          )}

          <div className="div-tca-five-1">
            <div className="div-tca-cpf_aprendiz">
              <label htmlFor="cpf_aprendiz">CPF do Aprendiz:</label>
              <input
                type="text"
                name="cpf_aprendiz"
                id="cpf_aprendiz"
                maxLength={14}
                onChange={handleChange}
                value={formData.cpf_aprendiz || ""}
              />
            </div>

            <div className="div-tca-five-1-nome">
              <label htmlFor="nome_aprendiz">Nome Completo:</label>
              <input
                type="text"
                name="nome_aprendiz"
                id="nome_aprendiz"
                maxLength={100}
                onChange={handleChange}
                value={formData.nome_aprendiz}
              />
            </div>

            <div className="div-informacoes-contato">
              <label htmlFor="telefone_contato">
                Telefone/Informações adicionais de contato:
              </label>
              <InputMask
                mask="(99)99999-9999"
                maskChar=" "
                type="text"
                name="telefone_contato"
                id="telefone_contato"
                autoComplete="off"
                onChange={handleChange}
                value={formData.telefone_contato || ""}
              />
            </div>

            <div className="div-tca-five-1-email">
              <label htmlFor="email_aprendiz">E-mail:</label>
              <input
                type="email"
                name="email_aprendiz"
                id="email_aprendiz"
                maxLength={100}
                onChange={handleChange}
                value={formData.email_aprendiz}
              />
            </div>
          </div>

          {estudanteNaoEncontrado && (
            <div style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "1.25rem 1.5rem",
              marginBottom: "1.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "24px",
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
              flexWrap: "wrap"
            }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", flex: "1 1 300px" }}>
                <span style={{ fontWeight: "700", color: "#1e293b", fontSize: "0.95rem" }}>
                  Precisa de ajuda com o cadastro?
                </span>
                <span style={{ fontSize: "0.85rem", color: "#475569", lineHeight: "1.5" }}>
                  Em caso de dúvida entre em contato com nossa empresa através.<br />
                  Telefone/Whatsapp: <strong>(31) 3428100</strong> ; E-mail: <a href="mailto:atendimento@cieemg.org.br" style={{ color: "#185fa5", textDecoration: "none", fontWeight: "600" }}>atendimento@cieemg.org.br</a>
                </span>
              </div>

              <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => setCienteCadastro(true)}
                  disabled={cienteCadastro}
                  style={{
                    backgroundColor: cienteCadastro ? "#dcfce7" : "#f1f5f9",
                    color: cienteCadastro ? "#166534" : "#475569",
                    border: cienteCadastro ? "1px solid #bbf7d0" : "1px solid #cbd5e1",
                    borderRadius: "8px",
                    padding: "0.75rem 1.25rem",
                    fontWeight: "600",
                    fontSize: "0.95rem",
                    cursor: cienteCadastro ? "default" : "pointer",
                    transition: "all 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  {cienteCadastro ? (
                    <>
                      <Check size={18} />
                      Ciente
                    </>
                  ) : (
                    "Estou Ciente da Situação"
                  )}
                </button>

                <button
                  type="button"
                  disabled={!cienteCadastro}
                  onClick={() => window.open('https://appcadastro.cieemg.org.br/', '_blank')}
                  style={{
                    backgroundColor: cienteCadastro ? "#185fa5" : "#94a3b8",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "8px",
                    padding: "0.75rem 1.75rem",
                    fontWeight: "600",
                    fontSize: "0.95rem",
                    cursor: cienteCadastro ? "pointer" : "not-allowed",
                    boxShadow: cienteCadastro ? "0 4px 6px rgba(24, 95, 165, 0.2)" : "none",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    opacity: cienteCadastro ? 1 : 0.7
                  }}
                  onMouseOver={(e) => {
                    if (!cienteCadastro) return;
                    e.target.style.backgroundColor = "#10477e";
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = "0 6px 8px rgba(24, 95, 165, 0.3)";
                  }}
                  onMouseOut={(e) => {
                    if (!cienteCadastro) return;
                    e.target.style.backgroundColor = "#185fa5";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 4px 6px rgba(24, 95, 165, 0.2)";
                  }}
                >
                  Fazer Cadastro do Aprendiz
                </button>
              </div>
            </div>
          )}

          <label htmlFor="div-tca-five-lbl" className="div-tca-five-lbl">
            Observações: <br />
            1)É de suma importância a inscrição do jovem em nosso site com
            antecedência. <br />
            2)Observar se o horário escolar é contrário ao horário de
            aprendizagem. <br />
            3)O início da aprendizagem só poder ocorrer após assinatura do
            contrato.
          </label>
        </div>

        <DocumentUploadForm
          schema={[
            {
              id: "anexo_comprovante_endereco",
              label: "Comprovante de Endereço",
              icon: "Home",
              required: true,
            },
            {
              id: "anexo_historico_conclusao",
              label:
                "Declaração escolar e/ou histórico de conclusão do Ensino Médio",
              hint: "Até 30 dias de emissão",
              icon: "GraduationCap",
              required: true,
            },
            {
              id: "anexo_documento_identificacao",
              label: "Documento de Identificação (RG ou CNH)",
              icon: "IdCard",
              required: true,
            },
          ]}
          filesById={anexosFiles}
          statusById={anexosStatus}
          onAnexar={(id, file) =>
            handleAnexoChange(id)({ target: { files: [file] } })
          }
          topNote="Gentileza anexar sua declaração escolar atualizada ou com no máximo 30 dias de emissão. Sem a declaração escolar sua co  ntratação não será efetivada."
          missingMessage="Anexe a declaração escolar atualizada, com no máximo 30 dias de emissão. Sem ela, sua contratação não será efetivada."
          observations={formData.observacoes2}
          insertObservations={showObs2}
          onInsertObservationsChange={(v) => {
            setShowObs2(v);
            setFormData((p) => ({
              ...p,
              inserir_observacoes_tca_comum: v,
              observacoes2: v ? p.observacoes2 : "",
            }));
          }}
          onObservationsChange={(v) => {
            const regex = /[^0-9a-zA-ZÀ-ÿ\s.,&-]/g;
            const filtered = String(v).replace(regex, "");
            setFormData((p) => ({ ...p, observacoes2: filtered }));
          }}
          showObservationsSection={permissao?.podeVerObservacoesCiee}
        />
        <div className="div-dados-aprendizagem-title">
          <label htmlFor="dados-aprendizagem-title">
            Dados da Aprendizagem
          </label>
        </div>

        <div className="div-tca-four">
          <div className="div-tca-three-row">
            <div className="div-data-inicio-contrato">
              <label className="all-label" htmlFor="data_inicio_contrato">
                Data do início do contrato:
              </label>
              <select
                name="data_inicio_contrato"
                id="data_inicio_contrato"
                disabled={!permissao?.podeEditarEmpresa}
                value={formData.data_inicio_contrato || ""}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    data_inicio_contrato: e.target.value,
                  }));
                }}
              >
                <option value="">Selecione</option>
                {datasVigenciaFuturas.map((d) => (
                  <option key={d.id} value={d.valorObj.data}>
                    {new Date(d.valorObj.data + "T00:00:00").toLocaleDateString(
                      "pt-BR",
                    )}
                  </option>
                ))}
              </select>
            </div>

            <div className="div-data-fim-contrato" style={{ width: "100%" }}>
              <label className="all-label" htmlFor="data_fim_contrato">
                Vigência Final:
              </label>
              <input
                type="date"
                name="data_fim_contrato"
                id="data_fim_contrato"
                value={formData.data_fim_contrato || ""}
                onChange={(e) => {
                  const novaData = e.target.value;
                  if (novaData && formData.data_nascimento) {
                    const novaParts = novaData.split("-");
                    const nascParts = formData.data_nascimento
                      .substring(0, 10)
                      .split("-");
                    if (novaParts.length === 3 && nascParts.length === 3) {
                      const dtNova = new Date(
                        parseInt(novaParts[0], 10),
                        parseInt(novaParts[1], 10) - 1,
                        parseInt(novaParts[2], 10),
                      );
                      const limiteIdade = new Date(
                        parseInt(nascParts[0], 10) + 24,
                        parseInt(nascParts[1], 10) - 1,
                        parseInt(nascParts[2], 10),
                      );
                      limiteIdade.setDate(limiteIdade.getDate() - 1); // 1 dia antes

                      if (dtNova > limiteIdade) {
                        toastError(
                          "A vigência deve terminar antes de o aprendiz completar 24 anos.",
                        );
                        setFormData({
                          ...formData,
                          data_fim_contrato: "",
                        });
                        return;
                      }
                    }
                  }
                  setFormData({
                    ...formData,
                    data_fim_contrato: novaData,
                  });
                }}
                disabled={
                  !permissao?.podeEditarEmpresa || submittingRef.current
                }
              />
            </div>

            <div className="horario-pratica">
              <label className="all-label">
                Horário da aprendizagem prática:
              </label>
              <div className="horario-box">
                <input
                  type="text"
                  name="horario_inicio_horas"
                  maxLength={2}
                  value={formData.horario_inicio_horas || ""}
                  disabled={!permissao?.podeEditarEmpresa}
                  onChange={(e) => {
                    const { valor, bloqueado } = filtrarHorarioParcial(
                      "horario_inicio_horas",
                      formData.horario_inicio_horas,
                      e.target.value,
                    );
                    setFormData((prev) => ({
                      ...prev,
                      horario_inicio_horas: valor,
                    }));
                    if (bloqueado) {
                      setHorarioBloqueado((prev) => ({
                        ...prev,
                        horario_inicio_horas: true,
                      }));
                      toastWarning(
                        "Horário Invalido! Exemplo: 07:30–11:30 ou 13:30–17:30.",
                      );
                    }
                  }}
                  onFocus={() =>
                    setHorarioBloqueado((prev) => ({
                      ...prev,
                      horario_inicio_horas: false,
                    }))
                  }
                />
                {horarioBloqueado.horario_inicio_horas && (
                  <span
                    className="horario-bloqueado-icon"
                    title="Horário bloqueado"
                    aria-label="Horário bloqueado"
                  >
                    🔒
                  </span>
                )}
                <span>:</span>
                <input
                  type="text"
                  name="horario_inicio_minutos"
                  maxLength={2}
                  value={formData.horario_inicio_minutos || ""}
                  disabled={!permissao?.podeEditarEmpresa}
                  onChange={(e) => {
                    const { valor, bloqueado } = filtrarHorarioParcial(
                      "horario_inicio_minutos",
                      formData.horario_inicio_minutos,
                      e.target.value,
                    );
                    setFormData((prev) => ({
                      ...prev,
                      horario_inicio_minutos: valor,
                    }));
                    if (bloqueado) {
                      setHorarioBloqueado((prev) => ({
                        ...prev,
                        horario_inicio_minutos: true,
                      }));
                      toastWarning(
                        "Horário bloqueado! Use apenas 07:30–11:30 ou 13:30–17:30.",
                      );
                    }
                  }}
                  onFocus={() =>
                    setHorarioBloqueado((prev) => ({
                      ...prev,
                      horario_inicio_minutos: false,
                    }))
                  }
                />
                {horarioBloqueado.horario_inicio_minutos && (
                  <span
                    className="horario-bloqueado-icon"
                    title="Horário bloqueado"
                    aria-label="Horário bloqueado"
                  >
                    🔒
                  </span>
                )}
                <span>às</span>
                <input
                  type="text"
                  name="horario_fim_horas"
                  maxLength={2}
                  value={formData.horario_fim_horas || ""}
                  disabled={!permissao?.podeEditarEmpresa}
                  onChange={(e) => {
                    const { valor, bloqueado } = filtrarHorarioParcial(
                      "horario_fim_horas",
                      formData.horario_fim_horas,
                      e.target.value,
                    );
                    setFormData((prev) => ({
                      ...prev,
                      horario_fim_horas: valor,
                    }));
                    if (bloqueado) {
                      setHorarioBloqueado((prev) => ({
                        ...prev,
                        horario_fim_horas: true,
                      }));
                      toastWarning(
                        "Horário bloqueado! Use apenas 07:30–11:30 ou 13:30–17:30.",
                      );
                    }
                  }}
                  onFocus={() =>
                    setHorarioBloqueado((prev) => ({
                      ...prev,
                      horario_fim_horas: false,
                    }))
                  }
                />
                {horarioBloqueado.horario_fim_horas && (
                  <span
                    className="horario-bloqueado-icon"
                    title="Horário bloqueado"
                    aria-label="Horário bloqueado"
                  >
                    🔒
                  </span>
                )}
                <span>:</span>
                <input
                  type="text"
                  name="horario_fim_minutos"
                  maxLength={2}
                  value={formData.horario_fim_minutos || ""}
                  disabled={!permissao?.podeEditarEmpresa}
                  onChange={(e) => {
                    const { valor, bloqueado } = filtrarHorarioParcial(
                      "horario_fim_minutos",
                      formData.horario_fim_minutos,
                      e.target.value,
                    );
                    setFormData((prev) => ({
                      ...prev,
                      horario_fim_minutos: valor,
                    }));
                    if (bloqueado) {
                      setHorarioBloqueado((prev) => ({
                        ...prev,
                        horario_fim_minutos: true,
                      }));
                      toastWarning(
                        "Horário bloqueado! Use apenas 07:30–11:30 ou 13:30–17:30.",
                      );
                    }
                  }}
                  onFocus={() =>
                    setHorarioBloqueado((prev) => ({
                      ...prev,
                      horario_fim_minutos: false,
                    }))
                  }
                />
                {horarioBloqueado.horario_fim_minutos && (
                  <span
                    className="horario-bloqueado-icon"
                    title="Horário bloqueado"
                    aria-label="Horário bloqueado"
                  >
                    🔒
                  </span>
                )}
              </div>
              {erroHorarioAprendizagem && (
                <div
                  className="aviso-horario"
                  role="alert"
                  aria-live="polite"
                  data-testid="aviso-horario-aprendizagem"
                >
                  <AlertCircle size={18} />
                  <span>{erroHorarioAprendizagem}</span>
                </div>
              )}
            </div>

            <div className="horario-teorica">
              <label className="all-label" htmlFor="horario_teorica">
                Horário da aprendizagem teórica:
              </label>
              <select
                name="horario_teorica"
                id="horario_teorica"
                disabled={!permissao.podeEditarEmpresa}
                value={formData.horario_teorica || ""}
                onChange={handleChange}
              >
                <option value="" disabled>
                  Selecione
                </option>
                {horariosTeoricosDisponiveis.map((h) => (
                  <option key={h.id} value={formatHorarioTeoricoLabel(h)}>
                    {formatHorarioTeoricoLabel(h)}
                  </option>
                ))}
              </select>
              {!municipioAtualTca && (
                <small
                  style={{
                    display: "block",
                    marginTop: "4px",
                    color: "#666",
                  }}
                >
                  Selecione uma cidade acima para listar os horários
                  disponíveis.
                </small>
              )}
              {municipioAtualTca &&
                horariosTeoricosDisponiveis.length === 0 && (
                  <small
                    style={{
                      display: "block",
                      marginTop: "4px",
                      color: "#b45309",
                    }}
                  >
                    Nenhum horário cadastrado para esta cidade.
                  </small>
                )}
            </div>
          </div>

          <div className="div-tca-four-3">
            <div className="div-curso">
              <label className="all-label" htmlFor="curso">
                Curso:
              </label>
              <select
                name="curso"
                id="curso"
                disabled={!permissao.podeEditarEmpresa}
                value={formData.curso || ""}
                onChange={handleChange}
              >
                <option value="">Selecione</option>
                {cursosFiltrados.map((c) => {
                  const valorUnico = `${c.valorObj.curso} — ${c.valorObj.carga_horaria} (${c.valorObj.modalidade})`;
                  return (
                    <option key={c.id} value={valorUnico}>
                      {valorUnico}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="div-carga-horaria-curso">
              <label className="all-label" htmlFor="carga_horario_curso">
                Carga horária diária:
              </label>
              <select
                name="carga_horario_curso"
                id="carga_horario_curso"
                className="carga-horario-curso"
                disabled={!permissao.podeEditarEmpresa}
                value={formData.carga_horario_curso || ""}
                onChange={handleChange}
              >
                <option value="" disabled selected>
                  Selecione
                </option>
                <option value="4 horas diárias">4 horas diárias</option>
                <option value="6 horas diárias">6 horas diárias</option>
              </select>
            </div>
            <div className="div-tca-salario">
              <label className="all-label" htmlFor="salario">
                Salário:
              </label>
              <NumericFormat
                name="salario"
                className="tca-salario"
                id="salario"
                disabled={!permissao.podeEditarEmpresa}
                value={formData.salario || ""}
                onValueChange={(values) => {
                  const { floatValue } = values;
                  setFormData((prev) => ({
                    ...prev,
                    salario: floatValue ?? 0,
                  }));
                }}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale={true}
                prefix="R$ "
              />
            </div>
          </div>

          <div className="div-tca-four-4">
            <div className="div-tutor-aprendiz-empresa">
              <label className="all-label" htmlFor="tutor_aprendiz_empresa">
                Tutor da Empresa:
              </label>
              <input
                type="text"
                name="tutor_aprendiz_empresa"
                className="tutor-aprendiz-empresa"
                id="tutor_aprendiz_empresa"
                disabled={!permissao.podeEditarEmpresa}
                maxLength={100}
                onChange={handleChange}
                value={formData.tutor_aprendiz_empresa}
              />
            </div>
            <div className="div-cargo-tutor">
              <label className="all-label" htmlFor="cargo_tutor">
                Cargo do(a) tutor(a):
              </label>
              <input
                type="text"
                name="cargo_tutor"
                className="cargo-tutor"
                id="cargo_tutor"
                disabled={!permissao.podeEditarEmpresa}
                maxLength={70}
                onChange={handleChange}
                value={formData.cargo_tutor}
              />
            </div>
            <div className="div-nome-tutor">
              <label className="all-label" htmlFor="nome_tutor">
                Nome do responsável para receber as avaliações práticas:
              </label>
              <input
                type="text"
                name="nome_tutor"
                className="nome-tutor"
                id="nome_tutor"
                disabled={!permissao.podeEditarEmpresa}
                maxLength={70}
                onChange={handleChange}
                value={formData.nome_tutor}
              />
            </div>
            <div className="div-email-tutor">
              <label className="all-label" htmlFor="email_tutor">
                E-mail do responsável para receber as avaliações práticas:
              </label>
              <input
                type="email"
                name="email_tutor"
                className="email-tutor"
                id="email_tutor"
                disabled={!permissao.podeEditarEmpresa}
                maxLength={100}
                value={formData.email_tutor || ""}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="div-tca-four-5">
            <div className="div-endereco-aprendizagem">
              <label className="all-label" htmlFor="endereco_tutor">
                Endereço completo da aprendizagem prática:
              </label>
              <input
                type="text"
                name="endereco_tutor"
                className="endereco-aprendizagem"
                id="endereco_tutor"
                disabled={!permissao.podeEditarEmpresa}
                maxLength={200}
                onChange={handleChange}
                value={formData.endereco_tutor}
              />
            </div>
          </div>

          <div className="div-tca-four-5">
            <div className="div-endereco-teoria">
              <label className="all-label" htmlFor="endereco_atividade_teorica">
                Endereço da atividade teórica:
              </label>
              <input
                type="text"
                name="endereco_atividade_teorica"
                className="endereco-teorica bg-white-important"
                id="endereco_atividade_teorica"
                readOnly={![2, 3, 4].includes(Number(user?.acesso_autorizacao))}
                onChange={handleChange}
                value={formData.endereco_atividade_teorica || ""}
              />
            </div>
          </div>

          <div className="div-fundo-txa">
            <div className="div-txa-obs-ajuste">
              <label
                className="lbl-textarea-ativs-aprendiz"
                htmlFor="atividades_aprendiz"
              >
                Atividades do aprendiz na empresa:
              </label>
              <select
                className="tca-atividades-select"
                name="atividades_aprendiz"
                id="atividades_aprendiz"
                disabled={
                  permissao?.podeEditarTudo
                    ? false
                    : !formData.curso ||
                      (!permissao?.podeEditarEmpresa && !permissao?.podeEditarTudo)
                }
                value={formData.atividades_aprendiz || ""}
                onChange={isStudent ? undefined : handleChange}
                onMouseDown={isStudent ? (e) => e.preventDefault() : undefined}
                tabIndex={isStudent ? -1 : undefined}
                aria-readonly={isStudent}
                title={
                  isStudent ? "Campo somente leitura para estudante" : undefined
                }
                required
              >
                <option value="">
                  {formData.curso
                    ? "Selecione uma atividade"
                    : "Selecione um curso primeiro"}
                </option>
                {atividadesFiltradas.map((a) => (
                  <option key={a.id} value={a.atividade}>
                    {a.atividade}
                  </option>
                ))}
              </select>
            </div>

            {permissao?.podeVerObservacoesCiee && (
              <div
                className="div-observacoes"
                style={{
                  background: "rgba(30, 41, 59, 0.55)",
                  borderRadius: 10,
                  padding: "16px 18px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  border: "1px solid rgba(255, 255, 255, 0.06)",
                  boxSizing: "border-box",
                  marginTop: "1.25rem",
                  marginBottom: "1.5rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 4,
                      height: 16,
                      borderRadius: 2,
                      background: "#185fa5",
                    }}
                  />
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      margin: 0,
                      color: "#fff",
                      letterSpacing: 0.2,
                    }}
                  >
                    CIEE/MG - Particularidades para emissão do contrato
                  </p>
                </div>

                <div className="div-txa-obs-ajuste-1">
                  <textarea
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      borderRadius: 8,
                      border: "1px solid #cbd5e1",
                      padding: 10,
                      fontSize: 13,
                      fontFamily: "inherit",
                      resize: "none",
                      background: "#ffffff",
                      color: "#0f172a",
                      outline: "none",
                      transition: "border-color 0.18s ease",
                    }}
                    className="textarea-ativs-aprendiz"
                    name="textarea-ativs-aprendiz"
                    id="textarea-ativs-aprendiz"
                    placeholder="Este espaço deve ser utilizado para observações, particularidades ou informações adicionais para a emissão do contrato."
                    value={formData["textarea-ativs-aprendiz"]}
                    onChange={(e) => { handleChange(e); }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {idadeCalculada !== null && idadeCalculada < 18 && (
          <div className="div-tca-six">
            <div className="div-dados-aprendizagem-title">
              <label htmlFor="dados-aprendizagem-title">
                Responsável Legal do Aprendiz
                <small
                  style={{
                    marginLeft: "0.75rem",
                    fontWeight: "normal",
                    fontSize: "0.85rem",
                    opacity: 0.85,
                  }}
                >
                  (Aprendiz menor de idade — {idadeCalculada} anos)
                </small>
              </label>
            </div>

            <div className="div-tca-six-1">
              <div className="div-tca-six-nome tca-six-n">
                <label
                  className="all-label"
                  htmlFor="nome_aprendiz_menor_idade"
                >
                  Nome:
                </label>
                <input
                  type="text"
                  name="nome_aprendiz_menor_idade"
                  className="nome-aprendiz"
                  id="nome_aprendiz_menor_idade"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.nome_aprendiz_menor_idade}
                />
              </div>
              <div className="div-tca-six-telefone tca-six-t">
                <label
                  className="all-label"
                  htmlFor="telefone_aprendiz_menor_idade"
                >
                  Telefone:
                </label>
                <InputMask
                  mask="(99)99999-9999"
                  maskChar=" "
                  type="text"
                  name="telefone_aprendiz_menor_idade"
                  autoComplete="off"
                  className="tce-telefones"
                  id="telefone_aprendiz_menor_idade"
                  value={formData.telefone_aprendiz_menor_idade || ""}
                  disabled={!permissao.podeEditarEmpresa}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="div-tca-six-email tca-six-e">
                <label
                  className="all-label"
                  htmlFor="email_aprendiz_menor_idade"
                >
                  Email:
                </label>
                <input
                  type="email"
                  name="email_aprendiz_menor_idade"
                  className="nome-aprendiz"
                  id="email_aprendiz_menor_idade"
                  disabled={!permissao.podeEditarEmpresa}
                  maxLength={100}
                  onChange={handleChange}
                  value={formData.email_aprendiz_menor_idade}
                />
              </div>
              <div className="div-tca-six-cpf tca-six-c">
                <label className="all-label" htmlFor="cpf_aprendiz_menor_idade">
                  CPF:
                </label>
                <InputMask
                  mask="999.999.999-99"
                  maskChar=" "
                  type="text"
                  name="cpf_aprendiz_menor_idade"
                  className="cpf-aprendiz"
                  id="cpf_aprendiz_menor_idade"
                  disabled={!permissao.podeEditarEmpresa}
                  onChange={handleChange}
                  required
                  value={formData.cpf_aprendiz_menor_idade}
                />
              </div>
            </div>
          </div>
        )}

        <div className="div-tca-seven">
          <div className="btn-wrapper">
            <button
              type="button"
              onClick={openReview}
              className="btn-cadastrar-tca"
            >
              Cadastrar
            </button>
            {permissao?.podeAprovar &&
              formData.status !== "APROVADO" &&
              formData.status !== "RECUSADO" && (
                <>
                  <button
                    type="button"
                    onClick={aprovar}
                    className="btn-aprovar"
                  >
                    Aprovar
                  </button>
                  <button
                    type="button"
                    onClick={recusar}
                    className="btn-recusar"
                  >
                    Recusar
                  </button>
                </>
              )}
          </div>
        </div>
      </form>

      {showReview && (
        <div
          className="tca-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowReview(false);
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="tca-modal-content">
            <FormReview
              data={buildReviewData()}
              onEdit={() => setShowReview(false)}
              onSubmit={async (e) => {
                setShowReview(false);
                await handleSubmit(e);
              }}
            />
          </div>
        </div>
      )}

      {missingFieldsAlert.length > 0 && (
        <MissingFieldsAlert
          missingFields={missingFieldsAlert}
          onFieldClick={handleMissingFieldClick}
          onClose={() => setMissingFieldsAlert([])}
        />
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {confirmDialogComum}
    </>
  );
};

export default TCA;
